/**
 * ContentEditor — extracted from TeacherDashboard.js (Feb 2026 refactor).
 *
 * The full editing surface a teacher sees after picking a session from the
 * Studio Home: framework sidebar, hero, all FrameworkSections, the AI chat
 * dock, the 10-theme preview modal, publish + republish modals, board
 * capture, equation studio and image gen helpers.
 *
 * This is intentionally a thick component (≈2200 lines) — it owns the live
 * editing draft and orchestrates the modals + chat. Splitting it further
 * would require lifting an enormous amount of state into the parent without
 * a corresponding UX gain. Future refactors should pull pure sub-views
 * (PreviewModal, ChatDock, EditorToolbar) into their own files instead.
 *
 * Props (kept identical to the in-file definition before the refactor):
 *   • draft        the draft document being edited
 *   • setDraft     parent setter — used to push final saves back up
 *   • classes      the teacher's classes (for the publish picker)
 *   • reload       refetch parent draft list (post-publish)
 *   • goBack       navigate back to Studio Home
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';
import axios from 'axios';
import {
  Shield, Copy, Hash, Mail, UserPlus, ArrowLeft, Bell, MessageSquare, Plus, X, Loader2,
  Send, Sparkles, FileText, BookOpen, Trash2, Edit, ChevronDown, ChevronRight, ChevronLeft,
  Upload, Mic, MicOff, Square, Play, Pause, Download, Search, Filter,
  CheckCircle, AlertCircle, Clock, Users, BarChart3, Settings as SettingsIcon, LogOut,
  Eye, EyeOff, Save, RefreshCw, ImageIcon, Link2, Volume2, Calendar, ListChecks,
  Layers, Layout, Camera, Headphones, Briefcase, GraduationCap, Brain,
  Library, BookMarked, ScrollText, Star, ArrowRight, FileVideo,
  Sun, Moon, Image, AlignLeft, Tag, Newspaper, PlayCircle,
  Lightbulb, Target, Globe, Quote, Hash as HashIcon, FileCode, FunctionSquare, Atom,
  Beaker, FlaskConical, Undo2, Redo2, Sigma, Paperclip, Wand2, Edit2,
} from 'lucide-react';

import { Input } from './../../ui/input';
import { Button } from './../../ui/button';
import RenderContent, { MathText } from './../../RenderContent';
import PublishToLessonModal from './../../PublishToLessonModal';

import BoardCapturePanel from './../BoardCapturePanel';
import EquationStudio from './../EquationStudio';
import ImageGenHelper from './../ImageGenHelper';
import SectionEquations from './../SectionEquations';
import ModelPicker, { ModelBadge } from './../../ModelPicker';
import ReferencePopover from './../../ReferencePopover';
import FrameworkSection from './FrameworkSection';
import VisualEditorModal from './VisualEditorModal';
import SmartNoteBoardPanel from './SmartNoteBoardPanel';
import SessionPreview from './SessionPreview';
import { getSectionIcon } from '../../../lib/sectionIcons';

const API = process.env.REACT_APP_BACKEND_URL;
const G = '#34A853';

// ────────────────────────────────────────────────────────────────────────────
// Local helpers — kept private to ContentEditor. Mirror their counterparts in
// TeacherDashboard.js. If a definition diverges, update BOTH.
// ────────────────────────────────────────────────────────────────────────────
const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api/teacher${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; }
  catch { return { detail: text || `HTTP ${res.status}`, _status: res.status }; }
};

const apiUpload = async (path, fd) => {
  const token = localStorage.getItem('desky_token');
  const res = await fetch(`${API}/api/teacher${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  return res.json();
};

// Section icons come from the shared registry in lib/sectionIcons.

const DIFF = {
  easy:   { bg: '#DCFCE7', text: '#166534', l: 'Easy' },
  medium: { bg: '#FEF9C3', text: '#854D0E', l: 'Medium' },
  hard:   { bg: '#FEE2E2', text: '#991B1B', l: 'Hard' },
};

const TABLE_DEFAULT_COLORS = {
  header_bg: '#0d9488', header_text: '#ffffff', accent: '#0d9488',
  odd_row_bg: '#ffffff', even_row_bg: '#f0fdfa', text: '#1e293b', border: '#0d9488',
};
const resolveTableColors = (table) => ({ ...TABLE_DEFAULT_COLORS, ...(table?.colors || {}) });

const TABLE_THEMES = [
  { id: 'emerald', label: 'Emerald', colors: { header_bg: '#ecfdf5', header_text: '#065f46', accent: '#34A853', odd_row_bg: '#ffffff', even_row_bg: '#f0fdf4', text: '#1e293b', border: '#34A853' } },
  { id: 'sapphire', label: 'Sapphire', colors: { header_bg: '#eff6ff', header_text: '#1e3a8a', accent: '#3b82f6', odd_row_bg: '#ffffff', even_row_bg: '#f0f9ff', text: '#1e293b', border: '#3b82f6' } },
  { id: 'amethyst', label: 'Amethyst', colors: { header_bg: '#faf5ff', header_text: '#6b21a8', accent: '#8b5cf6', odd_row_bg: '#ffffff', even_row_bg: '#f5f3ff', text: '#1e293b', border: '#8b5cf6' } },
  { id: 'ruby', label: 'Ruby', colors: { header_bg: '#fef2f2', header_text: '#991b1b', accent: '#dc2626', odd_row_bg: '#ffffff', even_row_bg: '#fff1f2', text: '#1e293b', border: '#dc2626' } },
  { id: 'amber', label: 'Amber', colors: { header_bg: '#fffbeb', header_text: '#78350f', accent: '#d97706', odd_row_bg: '#ffffff', even_row_bg: '#fef3c7', text: '#1e293b', border: '#d97706' } },
  { id: 'teal', label: 'Teal', colors: { header_bg: '#f0fdfa', header_text: '#134e4a', accent: '#0d9488', odd_row_bg: '#ffffff', even_row_bg: '#ccfbf1', text: '#1e293b', border: '#0d9488' } },
  { id: 'rose', label: 'Rose', colors: { header_bg: '#fff1f2', header_text: '#881337', accent: '#e11d48', odd_row_bg: '#ffffff', even_row_bg: '#ffe4e6', text: '#1e293b', border: '#e11d48' } },
  { id: 'slate', label: 'Slate', colors: { header_bg: '#f1f5f9', header_text: '#0f172a', accent: '#475569', odd_row_bg: '#ffffff', even_row_bg: '#f8fafc', text: '#1e293b', border: '#475569' } },
  { id: 'midnight', label: 'Midnight', colors: { header_bg: '#1e293b', header_text: '#f1f5f9', accent: '#38bdf8', odd_row_bg: '#0f172a', even_row_bg: '#1e293b', text: '#e2e8f0', border: '#334155' } },
];

// Resizable chat panel width bounds (px). MIN must be wide enough for the
// composer's bottom toolbar (symbol/reference icons + model picker + send
// button, and its 300px-wide dropdown) to render without clipping against
// the panel's own overflow-hidden edge — see chatWidth usage below.
const MIN_CHAT_WIDTH = 410;
const MAX_CHAT_WIDTH = 720;
const DEFAULT_CHAT_WIDTH = 480;
const clampChatWidth = (w) => Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, w));

// Chat slash-commands surfaced in the @ /-mention menu.
const COMMANDS = [
  { cmd: '/add', label: 'Add content', desc: 'Add new content to section' },
  { cmd: '/edit', label: 'Edit content', desc: 'Modify existing content' },
  { cmd: '/simplify', label: 'Simplify', desc: 'Make content simpler' },
  { cmd: '/expand', label: 'Expand', desc: 'Add more detail' },
  { cmd: '/examples', label: 'Add examples', desc: 'Add real-world examples' },
  { cmd: '/quiz', label: 'Add quiz', desc: 'Add practice questions' },
  { cmd: '/summarize', label: 'Summarize', desc: 'Create a summary' },
  { cmd: '/equation', label: 'Insert equation', desc: 'Generate a LaTeX equation for this section' },
  { cmd: '/derive', label: 'Derive', desc: 'Show step-by-step derivation' },
  { cmd: '/solve', label: 'Solve step-by-step', desc: 'Worked solution with units' },
  { cmd: '/units', label: 'Add units & dimensions', desc: 'Annotate variables with SI units' },
  { cmd: '/balance', label: 'Balance reaction', desc: 'Balance a chemistry equation' },
  { cmd: '/reaction', label: 'Explain reaction', desc: 'Reactants → products with mechanism' },
  { cmd: '/diagram', label: 'Generate diagram', desc: 'AI image — labeled diagram' },
  { cmd: '/graph', label: 'Plot / graph', desc: 'Coordinate plot or function graph' },
  { cmd: '/board', label: 'Open board photos', desc: 'Add blackboard photos + OCR fusion' },
];

// @all — attaches every section as chat context, for edits and questions alike.
const ALL_SECTIONS_OPTION = { id: '__all__', title: 'All Sections', type: 'all', icon: 'layers' };

// Subject-adaptive prompt suggestions for the empty AI chat state.
function getSubjectSuggestions(subject, sections) {
  const first = sections?.[0]?.title || 'Overview';
  const second = sections?.[1]?.title || 'Key Concepts';
  const s = (subject || '').toLowerCase();
  if (['math', 'mathematics'].includes(s)) return [
    { label: `@${first} /equation insert main formula`, type: 'math' },
    { label: `@${first} /solve a worked example`,        type: 'math' },
    { label: `@${second} /graph plot the function`,       type: 'math' },
    { label: `@${first} /derive the proof step-by-step`,  type: 'math' },
  ];
  if (['physics'].includes(s)) return [
    { label: `@${first} /equation Newton's 2nd law with units`, type: 'math' },
    { label: `@${first} /diagram free-body diagram`,           type: 'image' },
    { label: `@${second} /solve numerical with SI units`,       type: 'math' },
    { label: `@${first} /units list variables and SI units`,    type: 'math' },
  ];
  if (['chemistry'].includes(s)) return [
    { label: `@${first} /balance the chemical equation`,        type: 'chem' },
    { label: `@${first} /reaction explain mechanism`,           type: 'chem' },
    { label: `@${second} /diagram lab apparatus setup`,         type: 'image' },
    { label: `@${first} /equation rate law expression`,         type: 'chem' },
  ];
  if (['biology'].includes(s)) return [
    { label: `@${first} /diagram labeled cell diagram`,         type: 'image' },
    { label: `@${first} /expand with real-world examples`,      type: 'text' },
    { label: `@${second} simplify for grade 9`,                 type: 'text' },
    { label: `@${first} /quiz 5 practice questions`,            type: 'text' },
  ];
  if (['computer_science', 'cs', 'programming', 'computing'].includes(s)) return [
    { label: `@${first} add a code example`,                    type: 'text' },
    { label: `@${first} explain the algorithm step-by-step`,    type: 'text' },
    { label: `@${second} /diagram flowchart`,                   type: 'image' },
    { label: `@${first} /quiz 5 practice questions`,            type: 'text' },
  ];
  return [
    { label: `@${first} simplify for grade 9`,                    type: 'text' },
    { label: `/expand with real-world examples`,                  type: 'text' },
    { label: `@${second} /diagram create illustration`,           type: 'image' },
    { label: `@${first} /quiz 5 practice questions`,              type: 'text' },
  ];
}


/* ═══════════════════════════════════════════════════════════════════════════
   CONTENT EDITOR — Framework + AI Chat with @ / commands
   ═══════════════════════════════════════════════════════════════════════════ */
function ContentEditor({ draft: initialDraft, setDraft: setParentDraft, classes, reload, goBack }) {
  const [draft, setDraft] = useState(initialDraft);
  const [chatInput, setChatInput] = useState('');
  // Reference chips — URLs or previously-published lessons that the teacher
  // attaches as additional context for the next AI message. Appended as a
  // compact "References:" footer at send time. Cleared per-message.
  const [refChips, setRefChips] = useState([]);
  const [refLibrary, setRefLibrary] = useState([]);
  // Image staged via the chat composer's "import image" button — sent to the
  // AI alongside the typed prompt in the same message, then cleared.
  const [chatImage, setChatImage] = useState(null);
  const [uploadingChatImage, setUploadingChatImage] = useState(false);
  const [chatModel, setChatModel] = useState(() => {
    try { return localStorage.getItem('desky_chat_model') || 'auto'; } catch { return 'auto'; }
  });
  const onChatModelChange = (k) => { setChatModel(k); try { localStorage.setItem('desky_chat_model', k); } catch { /* ignore storage errors */ } };
  const [urlInput, setUrlInput] = useState('');
  const [processing, setProcessing] = useState('');
  const [showPublish, setShowPublish] = useState(false);
  const [showLessonPublish, setShowLessonPublish] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [commandFilter, setCommandFilter] = useState('');
  const [menuIndex, setMenuIndex] = useState(0);
  const [targetSection, setTargetSection] = useState(null);
  // Inline edit of a previous chat message ({ idx, text }) — submits as a
  // LangGraph branch re-run from that turn's checkpoint.
  // New states for advanced features
  const [blackboardAnalysis, setBlackboardAnalysis] = useState(draft.blackboard_analysis || null);
  const [showBlackboardPanel, setShowBlackboardPanel] = useState(false);
  const [showRefSuggestions, setShowRefSuggestions] = useState(false);
  const [refSuggestions, setRefSuggestions] = useState([]);
  const [addingRefIdx, setAddingRefIdx] = useState(null); // index of the suggestion being attached
  const refSuggestionsRef = useRef(null); // scroll target for the suggestions panel
  // Resizable editor/chat split — chat panel width in px, persisted per browser.
  const [chatWidth, setChatWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('desky_chat_width') || '', 10);
    return Number.isFinite(saved) ? clampChatWidth(saved) : DEFAULT_CHAT_WIDTH;
  });
  const [titleEditMode, setTitleEditMode] = useState(false);
  const [refUrlOpen, setRefUrlOpen] = useState(false);
  const [editingItems, setEditingItems] = useState({}); // {sectionId: itemIndex}
  const [showPreview, setShowPreview] = useState(false);
  const [humanizing, setHumanizing] = useState(false);
  const [visualModal, setVisualModal] = useState(null); // section for full-screen visual editor
  const [equationModal, setEquationModal] = useState(false);
  const [imageHelperModal, setImageHelperModal] = useState(null); // { sectionId } or null
  const [boardPanelOpen, setBoardPanelOpen] = useState(false);
  // Adaptive framework classifier state
  const [taxonomy, setTaxonomy] = useState(null); // {grade_bands[], subjects[]}
  const [classification, setClassification] = useState(null); // {subject, grade_band, method}
  const [sectionPlan, setSectionPlan] = useState(null); // {core_sections, suggested_sections, ...}
  const [overrideSubject, setOverrideSubject] = useState(null);
  const [overrideGrade, setOverrideGrade] = useState(null);
  const [classifying, setClassifying] = useState(false);
  // Session-card fields shown on the framework header card. Mirrors the
  // Deskey Session card: dept/batch/subject follow the teacher's Settings
  // toggles; class & chapter always show.
  const [cardFields, setCardFields] = useState({ subject: true, batch: false, department: false });
  const [classMeta, setClassMeta] = useState(null); // {name, batch, department, subject} for draft.class_id
  const [chapterName, setChapterName] = useState('');
  const chatEndRef = useRef(null);
  const chatRef = useRef(null);
  const photoInputRef = useRef(null);
  const blackboardInputRef = useRef(null);
  const chatImageInputRef = useRef(null);

  const framework = draft.framework || null;

  // Load the teacher's session-card field toggles (saved to their account)
  useEffect(() => {
    api('/preferences/session-card').then(r => { if (r?.fields) setCardFields(f => ({ ...f, ...r.fields })); }).catch(() => {});
  }, []);
  // Land on the most recent message when a draft's chat history first
  // renders — without this the panel opens scrolled to the top (oldest
  // turns), same gap on the student side.
  useEffect(() => {
    if ((draft.chat_history || []).length > 0) {
      chatEndRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [draft.id]);
  // Resolve department / batch / class / subject for the draft's class
  useEffect(() => {
    if (!draft?.class_id) { setClassMeta(null); return; }
    api('/classes/grouped').then(r => {
      for (const g of r.groups || []) {
        for (const b of g.batches || []) {
          const c = (b.classes || []).find(x => x.id === draft.class_id);
          if (c) { setClassMeta({ name: c.name, batch: b.batch?.name || '', department: g.department?.name || '', subject: (c.subjects && c.subjects[0]) || '' }); return; }
        }
      }
      setClassMeta(null);
    }).catch(() => {});
  }, [draft?.class_id]);
  // Resolve the chapter (lesson) title the draft is filed under
  useEffect(() => {
    if (!draft?.class_id || !draft?.lesson_id) { setChapterName(''); return; }
    api(`/classes/${draft.class_id}/lessons`).then(r => {
      setChapterName(((r.lessons || []).find(x => x.id === draft.lesson_id) || {}).title || '');
    }).catch(() => {});
  }, [draft?.class_id, draft?.lesson_id]);
  // Auto-sync drafts (voice + board fusion) store section text as
  // items[{type:'text', value}] with no section.type — FrameworkSection only
  // renders body text for text/rich_text sections via `content`, so coerce
  // those sections into that shape or they display as empty cards.
  const sections = useMemo(() => (framework?.sections || []).map(sec => {
    if (sec.type) return sec;
    const content = (typeof sec.content === 'string' && sec.content.trim())
      ? sec.content
      : (sec.items || [])
          .filter(it => it?.type === 'text' && typeof it.value === 'string' && it.value.trim())
          .map(it => `• ${it.value.trim()}`)
          .join('\n');
    return { ...sec, type: 'rich_text', content };
  }), [framework]);
  const token = localStorage.getItem('desky_token') || '';

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  // Append a just-added section into local state so it renders immediately —
  // the add-section endpoints return the created section, but `reload` only
  // refreshes the parent draft list, not this editor's own `draft.framework`.
  const applyAddedSection = (section) => {
    if (!section?.id) return;
    setDraft(prev => {
      const fw = { ...(prev.framework || {}) };
      fw.sections = [...(fw.sections || []), section];
      return { ...prev, framework: fw };
    });
    setExpandedSections(prev => ({ ...prev, [section.id]: true }));
  };

  // Filtered items for mention/command menus. @all always shows first when
  // it matches the filter — it attaches every section as chat context.
  const filteredSections = useMemo(() => {
    const matches = sections.filter(s => s.title.toLowerCase().includes(mentionFilter.toLowerCase()));
    const filterLower = mentionFilter.toLowerCase();
    return ('all'.startsWith(filterLower) || 'all sections'.includes(filterLower))
      ? [ALL_SECTIONS_OPTION, ...matches] : matches;
  }, [sections, mentionFilter]);
  const filteredCommands = useMemo(() => COMMANDS.filter(c => c.cmd.toLowerCase().includes(commandFilter.toLowerCase()) || c.label.toLowerCase().includes(commandFilter.toLowerCase())), [commandFilter]);

  // Transcribe
  const handleTranscribe = async () => {
    setProcessing('transcribe');
    try {
      const res = await api(`/studio/transcribe/${draft.id}`, { method: 'POST' });
      if (res.transcript) { setDraft(prev => ({ ...prev, transcript: res.transcript })); toast.success('Transcription complete'); }
    } catch { toast.error('Failed'); }
    setProcessing('');
  };

  // Generate Framework (async)
  const handleGenerateFramework = async () => {
    setProcessing('framework');
    try {
      const params = new URLSearchParams();
      if (overrideSubject) params.set('subject', overrideSubject);
      if (overrideGrade) params.set('grade_band', overrideGrade);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api(`/studio/generate-framework/${draft.id}${qs}`, { method: 'POST' });
      if (res.status === 'generating') {
        toast('Generating framework...', { description: 'AI is analyzing your transcript (30-60s)' });
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          try {
            const status = await api(`/studio/framework-status/${draft.id}`);
            if (status.status === 'ready' && status.framework) {
              clearInterval(poll);
              setDraft(prev => ({ ...prev, framework: status.framework, framework_status: 'ready', key_topics: status.framework.sections?.slice(0, 5).map(s => s.title) || prev.key_topics }));
              const ae = {}; (status.framework.sections || []).slice(0, 3).forEach(s => { ae[s.id] = true; }); setExpandedSections(ae);
              if (status.framework.classification) setClassification(status.framework.classification);
              toast.success('Framework generated!');
              setProcessing('');
            } else if (attempts > 40) { clearInterval(poll); toast.error('Timeout'); setProcessing(''); }
          } catch { clearInterval(poll); setProcessing(''); }
        }, 3000);
      } else if (res.framework) {
        setDraft(prev => ({ ...prev, framework: res.framework }));
        const ae = {}; (res.framework.sections || []).slice(0, 3).forEach(s => { ae[s.id] = true; }); setExpandedSections(ae);
        toast.success('Framework generated!'); setProcessing('');
      }
    } catch { toast.error('Failed'); setProcessing(''); }
  };

  // Drafts arriving from Auto Sync "Generate Session" open while the adaptive
  // framework is still generating in the background — poll until it's ready.
  useEffect(() => {
    if (!draft?.id || draft?.framework || draft?.framework_status !== 'generating') return;
    setProcessing('framework');
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const status = await api(`/studio/framework-status/${draft.id}`);
        if (status.status === 'ready' && status.framework) {
          clearInterval(poll);
          setDraft(prev => ({ ...prev, framework: status.framework, framework_status: 'ready', key_topics: status.framework.sections?.slice(0, 5).map(s => s.title) || prev.key_topics }));
          const ae = {}; (status.framework.sections || []).slice(0, 3).forEach(s => { ae[s.id] = true; }); setExpandedSections(ae);
          if (status.framework.classification) setClassification(status.framework.classification);
          toast.success('Framework generated!');
          setProcessing('');
        } else if (attempts > 40) { clearInterval(poll); toast.error('Timeout'); setProcessing(''); }
      } catch { clearInterval(poll); setProcessing(''); }
    }, 3000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id, draft?.framework, draft?.framework_status]);

  // Auto-classify once we have a transcript (prepares pills before generation)
  const handleAutoClassify = useCallback(async () => {
    if (!draft?.id || !draft?.transcript || classifying) return;
    setClassifying(true);
    try {
      const res = await api(`/studio/classify/${draft.id}`, { method: 'POST' });
      if (res.classification) setClassification(res.classification);
      if (res.plan) setSectionPlan(res.plan);
    } catch { /* silent */ }
    finally { setClassifying(false); }
  }, [draft?.id, draft?.transcript, classifying]);

  // Re-fetch plan when overrides change
  const refreshPlanForOverride = useCallback(async () => {
    if (!overrideSubject && !overrideGrade) return;
    try {
      const res = await api('/studio/plan', {
        method: 'POST',
        body: JSON.stringify({
          subject: overrideSubject || classification?.subject || 'universal',
          grade_band: overrideGrade || classification?.grade_band || 'secondary',
        }),
      });
      if (res.plan) setSectionPlan(res.plan);
    } catch { /* silent */ }
  }, [overrideSubject, overrideGrade, classification]);

  useEffect(() => { refreshPlanForOverride(); }, [refreshPlanForOverride]);

  // Fetch taxonomy on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await api('/studio/taxonomy');
        if (res.grade_bands) setTaxonomy(res);
      } catch { /* silent */ }
    })();
  }, []);

  // Hydrate the Reference picker's library — every lesson across the
  // teacher's assigned classes. The `classes` prop from the parent dashboard
  // may be empty for teachers whose studio dashboard hasn't surfaced classes
  // yet, so we fall back to `/classes/my-assigned` directly. Cheap; once
  // per mount; silent on failure.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const lessons = [];
        const seen = new Set();
        // 1) Prefer the parent-passed classes if non-empty.
        let allClasses = (classes || []).filter(c => c?.id || c?.class_id);
        // 2) Fall back to /classes/my-assigned if parent passed nothing.
        if (allClasses.length === 0) {
          try {
            const r = await api('/classes/my-assigned');
            allClasses = (r?.classes || []).filter(c => c?.id || c?.class_id);
          } catch { /* silent */ }
        }
        await Promise.all(allClasses.map(async (c) => {
          const cid = c?.id || c?.class_id;
          if (!cid) return;
          try {
            const r = await api(`/classes/${cid}/lessons`);
            for (const l of (r?.lessons || [])) {
              if (l?.id && l?.title && !seen.has(l.id)) {
                seen.add(l.id);
                lessons.push({
                  id: l.id,
                  title: l.title,
                  subject: c.name || c.class_name || c.subject || '',
                });
              }
            }
          } catch { /* per-class — silent */ }
        }));
        if (mounted) setRefLibrary(lessons.slice(0, 40));
      } catch { /* silent */ }
    })();
    return () => { mounted = false; };
  }, [classes]);

  // Trigger auto-classify whenever transcript becomes available + no framework yet
  useEffect(() => {
    if (draft?.transcript && !draft?.framework && !classification) {
      handleAutoClassify();
    }
  }, [draft?.transcript, draft?.framework, classification, handleAutoClassify]);

  // Hydrate classification from loaded framework (returning to an already-generated draft)
  useEffect(() => {
    if (draft?.framework?.classification && !classification) {
      setClassification(draft.framework.classification);
      if (draft.framework.suggested_sections) {
        setSectionPlan(p => p || {
          core_sections: draft.framework.sections || [],
          suggested_sections: draft.framework.suggested_sections || [],
        });
      }
    }
  }, [draft?.framework, classification]);


  // Response types that actually mutate the draft — everything else
  // (conversation / answer / clarify / confirm / error) is text-only.
  const EDIT_RESPONSE_TYPES = ['image', 'table', 'section', 'content', 'multi_section'];

  // Core send — takes an explicit payload so both the composer (reading
  // live state) and the "Confirm" chat action (replaying a stored payload
  // with confirmed:true) share one code path.
  const sendChatMessage = async ({ message, targetSectionId, targetAll, confirmed, chatImages, chatReferences }) => {
    const attachmentCtx = (draft.references || []).map(r => ({ type: r.type, title: r.title, content: r.content || '', url: r.url || '' }));
    const localChat = {
      role: 'user', message, target_section: targetAll ? null : targetSectionId, target_all: !!targetAll,
      references: chatReferences && chatReferences.length > 0 ? chatReferences : undefined,
      // Full data URL for THIS session's optimistic echo only — the server
      // persists title-only (see chat_entry["images"] server-side) to avoid
      // storing unbounded base64 in the chat_history array long-term.
      images: chatImages && chatImages.length > 0 ? chatImages : undefined,
    };
    setProcessing('chat');
    // A Confirm-click resend replays the SAME request that's already visible
    // as the prior user bubble — don't echo it again as a second one; just
    // append the eventual result straight after the confirm question.
    if (!confirmed) setDraft(prev => ({ ...prev, chat_history: [...(prev.chat_history || []), localChat] }));
    const appendTurn = (prevHistory, assistantEntry) => (confirmed
      ? [...(prevHistory || []), assistantEntry]
      : [...(prevHistory || []).slice(0, -1), localChat, assistantEntry]);

    // Only a signal when exactly one section is expanded — with several open
    // (or none) it's not a meaningful hint about which one the teacher means.
    const expandedIds = Object.keys(expandedSections).filter(id => expandedSections[id]);
    const openSectionId = expandedIds.length === 1 ? expandedIds[0] : null;

    try {
      const res = await api('/studio/ai-chat', { method: 'POST', body: JSON.stringify({
        draft_id: draft.id, message, target_section_id: targetAll ? null : targetSectionId,
        target_all: !!targetAll, confirmed: !!confirmed,
        attachment_context: attachmentCtx.length > 0 ? attachmentCtx : null,
        chat_images: chatImages && chatImages.length > 0 ? chatImages : null,
        chat_references: chatReferences && chatReferences.length > 0
          ? chatReferences.map(r => ({ kind: r.kind, url: r.url, label: r.label }))
          : null,
        model_choice: chatModel,
        open_section_id: openSectionId,
      })});
      const aiMeta = {
        model_used: res.model_used || null, model_reason: res.model_reason || null,
        response_type: res.response_type, edit_id: res.edit_id || null, undoable: !!res.undoable,
        confirm_payload: res.confirm_payload || null,
        // LangGraph turn anchors: checkpoint_id = this turn's checkpoint,
        // checkpoint_base = the pre-turn checkpoint regenerate/edit branches from.
        checkpoint_id: res.checkpoint_id || null,
        checkpoint_base: res.checkpoint_base !== undefined ? res.checkpoint_base : undefined,
      };
      // Sync the context chip to whatever section the server actually
      // resolved this turn — covers both the tagged fast path (a no-op,
      // already matches) and an untagged message resolved by name, so a
      // plain-language follow-up ("add two more") stays visibly scoped
      // without the teacher ever typing @. Still dismissible/overridable —
      // this only runs once per response, not sticky beyond that.
      if (res.response_type !== 'error' && res.target_section_id
          && res.target_section_id !== targetSection?.id) {
        const resolved = sections.find(s => s.id === res.target_section_id);
        if (resolved) setTargetSection(resolved);
      }

      const pushAssistant = (text, extra = {}) => setDraft(prev => ({
        ...prev,
        chat_history: appendTurn(prev.chat_history, { role: 'assistant', message: text, ...aiMeta, ...extra }),
      }));

      if (res.response_type === 'image' && res.target_section_id) {
        // Image was generated and attached to section — refresh draft
        setDraft(prev => {
          const fw = { ...prev.framework };
          fw.sections = (fw.sections || []).map(s => {
            if (s.id === res.target_section_id) {
              const imgs = [...(s.section_images || [])];
              if (res.image_url) imgs.push({ url: res.image_url, caption: message, created_at: new Date().toISOString() });
              return { ...s, section_images: imgs };
            }
            return s;
          });
          return { ...prev, framework: fw, chat_history: appendTurn(prev.chat_history, { role: 'assistant', message: res.response, ...aiMeta }) };
        });
        toast.success('Image generated & attached!');
      } else if (res.response_type === 'table' && res.target_section_id && res.table_data) {
        // AI created a table — add it to the section's tables array
        setDraft(prev => {
          const fw = { ...prev.framework };
          fw.sections = (fw.sections || []).map(s => {
            if (s.id === res.target_section_id) {
              return { ...s, tables: [...(s.tables || []), res.table_data] };
            }
            return s;
          });
          return { ...prev, framework: fw, chat_history: appendTurn(prev.chat_history, { role: 'assistant', message: res.change_summary || `Table added to ${sections.find(s => s.id === res.target_section_id)?.title || 'section'}`, ...aiMeta }) };
        });
        toast.success('Table created!');
      } else if (res.response_type === 'multi_section' && Array.isArray(res.updated_sections)) {
        // @all edit — every touched section came back in one shot.
        setDraft(prev => ({ ...prev, framework: { ...prev.framework, sections: res.updated_sections } }));
        pushAssistant(res.response || res.change_summary || 'Sections updated');
        toast.success('Sections updated');
      } else if (res.target_section_id && res.response_type === 'section' && framework) {
        // Update the specific section
        setDraft(prev => {
          const fw = { ...prev.framework };
          fw.sections = (fw.sections || []).map(s => {
            if (s.id === res.target_section_id) {
              // Structured text/rich_text edits arrive as parsed fields —
              // intro text, objectives, takeaways, subsections — each applied
              // to its own section field. Never write the raw reply into
              // `content` when any of these are present: the raw reply may be
              // a JSON object or a labeled composite ("TEXT CONTENT: ...")
              // that would render as literal labels.
              if (Array.isArray(res.updated_subsections) || res.updated_section_content
                  || Array.isArray(res.updated_objectives) || Array.isArray(res.updated_takeaways)) {
                return {
                  ...s,
                  ...(Array.isArray(res.updated_subsections) ? { subsections: res.updated_subsections } : {}),
                  ...(res.updated_section_content ? { content: res.updated_section_content } : {}),
                  ...(Array.isArray(res.updated_objectives) ? { objectives: res.updated_objectives } : {}),
                  ...(Array.isArray(res.updated_takeaways) ? { takeaways: res.updated_takeaways } : {}),
                };
              }
              // Card/flashcard/problem/list/visual edits arrive as parsed items
              // from the backend (updated_items_field says where they live:
              // items | image_prompts); fall back to parsing updated_content
              // (which may be wrapped in ``` fences) for older backends.
              if (Array.isArray(res.updated_items)) return { ...s, [res.updated_items_field || 'items']: res.updated_items };
              try {
                const cleaned = (res.updated_content || '').trim().replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
                const items = JSON.parse(cleaned);
                if (Array.isArray(items)) return { ...s, items };
              } catch {}
              // Item-based sections render `items` (visual renders
              // `image_prompts`), not `content` — writing the text there
              // would hide the edit in a field that never displays.
              if (['cards', 'flashcards', 'problems', 'list', 'visual'].includes(s.type)) return s;
              return { ...s, content: res.updated_content };
            }
            return s;
          });
          return { ...prev, framework: fw, chat_history: appendTurn(prev.chat_history, { role: 'assistant', message: res.change_summary || `Updated ${sections.find(s => s.id === res.target_section_id)?.title || 'section'}`, ...aiMeta }) };
        });
        toast.success(`Section updated`);
      } else if (res.updated_content) {
        setDraft(prev => ({ ...prev, content: res.updated_content, chat_history: appendTurn(prev.chat_history, { role: 'assistant', message: res.change_summary || 'Content updated', ...aiMeta }) }));
        toast.success('Content updated');
      } else {
        // conversation / answer / clarify / confirm / error — nothing was
        // (or should be) applied. Surface the assistant's text as-is.
        pushAssistant(res.response || 'No changes were applied');
        if (res.response_type === 'error') toast.error('AI could not apply the edit — try rephrasing');
      }

      if (EDIT_RESPONSE_TYPES.includes(res.response_type)) {
        setCanUndo(true);
        setCanRedo(false);
        triggerAutosave();
      }
      return true;
    } catch {
      toast.error('AI edit failed');
      return false;
    } finally {
      setProcessing('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // AI Chat with section targeting — @ means "here is context", not "edit
  // this"; the assistant decides edit vs. converse vs. answer server-side.
  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    const refsForThisTurn = refChips;
    setRefChips([]);
    const imageForThisTurn = chatImage;
    setChatImage(null);
    setShowMentionMenu(false);
    setShowCommandMenu(false);

    const isAll = targetSection?.id === '__all__';
    const sectionId = (targetSection && !isAll) ? targetSection.id : null;
    // The tagged section/@all stays the active context for follow-ups — it's
    // only cleared by the dismiss (×) on the context chip or by tagging
    // something else.
    // Attached images/references ride as their own fields (chatImages /
    // chatReferences), not spliced into `message` — otherwise the teacher's
    // own chat bubble would show a raw "References:" block instead of what
    // they actually typed.
    const ok = await sendChatMessage({
      message: msg, targetSectionId: sectionId, targetAll: isAll,
      chatImages: imageForThisTurn ? [{ url: imageForThisTurn.url, title: imageForThisTurn.title }] : null,
      chatReferences: refsForThisTurn && refsForThisTurn.length > 0 ? refsForThisTurn : null,
    });
    if (!ok) {
      if (refsForThisTurn && refsForThisTurn.length) setRefChips(refsForThisTurn);
      if (imageForThisTurn) setChatImage(imageForThisTurn);
    }
  };

  // "Confirm" action on a destructive-edit chat prompt — replays the exact
  // original request with confirmed:true.
  const handleConfirmEdit = async (idx, payload) => {
    setDraft(prev => ({
      ...prev,
      chat_history: (prev.chat_history || []).map((m, i) => (i === idx ? { ...m, confirm_payload: null } : m)),
    }));
    await sendChatMessage({
      message: payload.message, targetSectionId: payload.target_section_id,
      targetAll: !!payload.target_all, confirmed: true,
    });
  };

  // "Undo this change" action on an edit's chat message — reverts just that
  // edit's section(s) via the pre-edit snapshot captured server-side.
  const handleUndoChatEdit = async (idx, editId) => {
    try {
      const res = await api(`/studio/drafts/${draft.id}/chat/undo`, { method: 'POST', body: JSON.stringify({ edit_id: editId }) });
      if (!res.undone) { toast.error('Could not undo that change'); return; }
      setDraft(prev => ({
        ...prev,
        ...(res.framework ? { framework: res.framework } : {}),
        ...(res.content != null ? { content: res.content } : {}),
        chat_history: (prev.chat_history || []).map((m, i) => (i === idx ? { ...m, undone: true } : m)),
      }));
      toast.success('Change undone');
    } catch { toast.error('Could not undo that change'); }
  };

  // Regenerate a response, or edit a previous message and regenerate — both
  // re-run the turn from its pre-turn LangGraph checkpoint (a new branch
  // server-side). The server rewrites framework + chat_history for the active
  // branch, so refetch the draft instead of patching locally.
  const handleRerunTurn = async (idx, newMessage = null) => {
    const history = draft.chat_history || [];
    const assistantMsg = history[idx];
    if (!assistantMsg || assistantMsg.checkpoint_base === undefined) {
      toast.error('This message predates branching support');
      return;
    }
    const userMsg = idx > 0 && history[idx - 1]?.role === 'user' ? history[idx - 1] : null;
    setProcessing('chat');
    try {
      await api(`/studio/drafts/${draft.id}/chat/rerun`, { method: 'POST', body: JSON.stringify({
        checkpoint_base: assistantMsg.checkpoint_base ?? null,
        message: newMessage,
        target_section_id: userMsg ? userMsg.target_section : null,
        target_all: userMsg ? !!userMsg.target_all : false,
        model_choice: chatModel,
      })});
      const fresh = await api(`/studio/drafts/${draft.id}`);
      setDraft(prev => ({ ...prev, ...fresh }));
      setCanUndo(true);
      setCanRedo(false);
    } catch {
      toast.error('Could not regenerate');
    } finally {
      setProcessing('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // Chat input handler with @mention and /command detection
  const handleChatInputChange = (e) => {
    const val = e.target.value;
    setChatInput(val);

    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);

    // Check for @mention
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionFilter(atMatch[1]);
      setShowMentionMenu(true);
      setShowCommandMenu(false);
      setMenuIndex(0);
      return;
    }

    // Check for /command
    const slashMatch = textBefore.match(/\/(\w*)$/);
    if (slashMatch) {
      setCommandFilter(slashMatch[1]);
      setShowCommandMenu(true);
      setShowMentionMenu(false);
      setMenuIndex(0);
      return;
    }

    setShowMentionMenu(false);
    setShowCommandMenu(false);
  };

  // Handle keyboard navigation in menus
  const handleChatKeyDown = (e) => {
    if (showMentionMenu || showCommandMenu) {
      const items = showMentionMenu ? filteredSections : filteredCommands;
      if (e.key === 'ArrowDown') { e.preventDefault(); setMenuIndex(prev => Math.min(prev + 1, items.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMenuIndex(prev => Math.max(prev - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (showMentionMenu && filteredSections[menuIndex]) { selectMention(filteredSections[menuIndex]); }
        if (showCommandMenu && filteredCommands[menuIndex]) { selectCommand(filteredCommands[menuIndex]); }
        return;
      }
      if (e.key === 'Escape') { setShowMentionMenu(false); setShowCommandMenu(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !showMentionMenu && !showCommandMenu) { e.preventDefault(); handleChatSend(); }
  };

  // Select @mention (section)
  const selectMention = (section) => {
    const cursor = chatRef.current?.selectionStart || chatInput.length;
    const textBefore = chatInput.slice(0, cursor);
    const textAfter = chatInput.slice(cursor);
    const newBefore = textBefore.replace(/@\w*$/, `@${section.title} `);
    setChatInput(newBefore + textAfter);
    setTargetSection(section);
    setShowMentionMenu(false);
    chatRef.current?.focus();
  };

  // Select /command
  const selectCommand = (cmd) => {
    const cursor = chatRef.current?.selectionStart || chatInput.length;
    const textBefore = chatInput.slice(0, cursor);
    const textAfter = chatInput.slice(cursor);
    const newBefore = textBefore.replace(/\/\w*$/, `${cmd.cmd} `);
    setChatInput(newBefore + textAfter);
    setShowCommandMenu(false);
    chatRef.current?.focus();
  };

  // URL Extract
  const handleExtractUrl = async () => {
    if (!urlInput.trim()) return;
    setProcessing('extract');
    try {
      const res = await api('/studio/extract-url', { method: 'POST', body: JSON.stringify({ url: urlInput.trim(), draft_id: draft.id }) });
      if (res.type) { setDraft(prev => ({ ...prev, references: [...(prev.references || []), res] })); setUrlInput(''); toast.success('Reference added'); }
    } catch { toast.error('Failed'); }
    setProcessing('');
  };

  // Photo Upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setProcessing('upload');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await apiUpload(`/studio/upload-photo/${draft.id}`, fd);
      if (res.type === 'photo') { setDraft(prev => ({ ...prev, references: [...(prev.references || []), res] })); toast.success('Photo uploaded'); }
    } catch { toast.error('Failed'); }
    setProcessing('');
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // Chat composer — "attach" button. Images reuse the same upload-photo
  // endpoint/logic as the top-toolbar Photo Upload button; PDFs go through
  // upload-chat-pdf, which extracts text server-side instead (no vision
  // model needed). Either way, the result is staged and sent alongside the
  // next chat message so the AI sees the attachment + the typed prompt
  // together.
  const handleChatAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploadingChatImage(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await apiUpload(`/studio/upload-chat-image/${draft.id}`, fd);
      if (res.type === 'photo') {
        setChatImage({ kind: 'image', url: res.url, title: res.title || file.name });
      }
    } catch { toast.error('Image upload failed'); }
    setUploadingChatImage(false);
    if (chatImageInputRef.current) chatImageInputRef.current.value = '';
  };

  // Blackboard Image Analysis
  const handleBlackboardUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setProcessing('blackboard');
    setShowBlackboardPanel(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const imageData = ev.target.result;
        try {
          const res = await api(`/studio/analyze-blackboard/${draft.id}`, {
            method: 'POST', body: JSON.stringify({ draft_id: draft.id, image_data: imageData })
          });
          if (res.status === 'analyzing') {
            toast('Analyzing blackboard...', { description: 'AI is reading handwritten content (20-40s)' });
            let attempts = 0;
            const poll = setInterval(async () => {
              attempts++;
              try {
                const status = await api(`/studio/blackboard-status/${draft.id}`);
                if (status.status === 'ready' && status.analysis) {
                  clearInterval(poll);
                  setBlackboardAnalysis(status.analysis);
                  setDraft(prev => ({ ...prev, blackboard_analysis: status.analysis, blackboard_status: 'ready' }));
                  toast.success('Blackboard analyzed!');
                  setProcessing('');
                } else if (attempts > 30) { clearInterval(poll); toast.error('Analysis timeout'); setProcessing(''); }
              } catch { clearInterval(poll); setProcessing(''); }
            }, 3000);
          }
        } catch { toast.error('Analysis failed'); setProcessing(''); }
      };
      reader.readAsDataURL(file);
    } catch { toast.error('Failed to read image'); setProcessing(''); }
    if (blackboardInputRef.current) blackboardInputRef.current.value = '';
  };

  // AI Reference Suggestions
  const handleSuggestReferences = async () => {
    setProcessing('refs');
    setShowRefSuggestions(true);
    try {
      const res = await api(`/studio/suggest-references/${draft.id}`, { method: 'POST' });
      if (res.suggestions) {
        setRefSuggestions(res.suggestions);
        toast.success('References suggested!');
        // Results render at the bottom of the page — bring them into view.
        setTimeout(() => refSuggestionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
      }
    } catch { toast.error('Failed'); }
    setProcessing('');
  };

  // `url_hint` is often search terms rather than a real URL — resolve it to
  // something clickable either way.
  const refSuggestionUrl = (ref) => {
    const hint = (ref.url_hint || '').trim();
    if (/^https?:\/\//i.test(hint)) return hint;
    return `https://www.google.com/search?q=${encodeURIComponent(hint || ref.title || '')}`;
  };

  // Add a suggested reference to the draft's Attachments (draft.references)
  const handleAddSuggestedRef = async (ref, idx) => {
    setAddingRefIdx(idx);
    try {
      const res = await api(`/studio/drafts/${draft.id}/references`, {
        method: 'POST',
        body: JSON.stringify({ title: ref.title, description: ref.description, type: ref.type, url: refSuggestionUrl(ref) }),
      });
      if (res.reference) {
        setDraft(prev => ({ ...prev, references: [...(prev.references || []), res.reference] }));
        toast.success('Added to attachments');
      } else toast.error(res.detail || 'Failed to add');
    } catch { toast.error('Failed to add'); }
    setAddingRefIdx(null);
  };

  // Drag the divider between the editor and the AI chat to resize both panes.
  const startChatResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = chatWidth;
    const onMove = (ev) => setChatWidth(clampChatWidth(startW + (startX - ev.clientX)));
    const onUp = (ev) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('desky_chat_width', String(clampChatWidth(startW + (startX - ev.clientX))));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Item-level CRUD
  const handleAddItem = async (sectionId, item) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/items`, {
        method: 'POST', body: JSON.stringify({ item })
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, items: [...(s.items || []), item] } : s);
        return { ...prev, framework: fw };
      });
      toast.success('Item added');
    } catch { toast.error('Failed to add item'); }
  };

  const handleDeleteItem = async (sectionId, itemIndex) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/items/${itemIndex}`, { method: 'DELETE' });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) { const items = [...(s.items || [])]; items.splice(itemIndex, 1); return { ...s, items }; }
          return s;
        });
        return { ...prev, framework: fw };
      });
      toast.success('Item removed');
    } catch { toast.error('Failed'); }
  };

  const handleUpdateItem = async (sectionId, itemIndex, updatedItem) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/items/${itemIndex}`, {
        method: 'PUT', body: JSON.stringify({ item: updatedItem })
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) { const items = [...(s.items || [])]; items[itemIndex] = updatedItem; return { ...s, items }; }
          return s;
        });
        return { ...prev, framework: fw };
      });
      toast.success('Item updated');
    } catch { toast.error('Failed to update'); }
  };

  // Delete section
  const handleDeleteSection = async (sectionId) => {
    if (!(await confirmDialog({ title: 'Delete section', message: 'Delete this section? This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return;
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}`, { method: 'DELETE' });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).filter(s => s.id !== sectionId);
        return { ...prev, framework: fw };
      });
      toast.success('Section deleted');
    } catch { toast.error('Failed to delete section'); }
  };

  // Remove a section image
  const handleRemoveSectionImage = async (sectionId, imageIndex) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/remove-image/${imageIndex}`, { method: 'POST' });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) {
            const imgs = [...(s.section_images || [])];
            imgs.splice(imageIndex, 1);
            return { ...s, section_images: imgs };
          }
          return s;
        });
        return { ...prev, framework: fw };
      });
      toast.success('Image removed');
    } catch { toast.error('Failed to remove image'); }
  };

  // Resize a manually-attached section image (width %, cycled by the UI) —
  // mirrors teacher-mobile's SectionMedia resize control against the same
  // backend endpoint.
  const handleUpdateSectionImage = async (sectionId, imageIndex, patch) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/update-image/${imageIndex}`, {
        method: 'PUT', body: JSON.stringify(patch)
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) {
            const imgs = [...(s.section_images || [])];
            imgs[imageIndex] = { ...imgs[imageIndex], ...patch };
            return { ...s, section_images: imgs };
          }
          return s;
        });
        return { ...prev, framework: fw };
      });
    } catch { toast.error('Failed to resize image'); }
  };

  const handleRemoveGeneratedImage = (sectionId, imageIndex) => {
    setDraft(prev => {
      const fw = { ...prev.framework };
      fw.sections = (fw.sections || []).map(s => {
        if (s.id === sectionId) {
          const imgs = [...(s.generated_images || [])];
          imgs.splice(imageIndex, 1);
          return { ...s, generated_images: imgs };
        }
        return s;
      });
      return { ...prev, framework: fw };
    });
    // Persist to backend
    api(`/studio/framework/${draft.id}/section/${sectionId}`, {
      method: 'PUT',
      body: JSON.stringify({ generated_images: (draft.framework?.sections?.find(s => s.id === sectionId)?.generated_images || []).filter((_, i) => i !== imageIndex) })
    }).catch(() => {});
    toast.success('Image deleted');
  };

  // Section link handlers
  const handleAddSectionLink = async (sectionId, linkData) => {
    try {
      const res = await api(`/studio/framework/${draft.id}/section/${sectionId}/add-link`, {
        method: 'POST', body: JSON.stringify(linkData)
      });
      if (res.added) {
        setDraft(prev => {
          const fw = { ...prev.framework };
          fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, section_links: [...(s.section_links || []), res.link] } : s);
          return { ...prev, framework: fw };
        });
        toast.success('Link attached');
      }
    } catch { toast.error('Failed to add link'); }
  };

  const handleRemoveSectionLink = async (sectionId, linkIndex) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/remove-link/${linkIndex}`, { method: 'POST' });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) {
            const links = [...(s.section_links || [])];
            links.splice(linkIndex, 1);
            return { ...s, section_links: links };
          }
          return s;
        });
        return { ...prev, framework: fw };
      });
      toast.success('Link removed');
    } catch { toast.error('Failed to remove link'); }
  };

  // Section table handlers
  const handleAddSectionTable = async (sectionId, tableData) => {
    try {
      const res = await api(`/studio/framework/${draft.id}/section/${sectionId}/add-table`, {
        method: 'POST', body: JSON.stringify(tableData)
      });
      if (res.added) {
        setDraft(prev => {
          const fw = { ...prev.framework };
          fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, tables: [...(s.tables || []), res.table] } : s);
          return { ...prev, framework: fw };
        });
        toast.success('Table added');
      }
    } catch { toast.error('Failed to add table'); }
  };

  const handleUpdateSectionTable = async (sectionId, tableIndex, tableData) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/update-table/${tableIndex}`, {
        method: 'PUT', body: JSON.stringify(tableData)
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) {
            const tables = [...(s.tables || [])];
            tables[tableIndex] = { ...tables[tableIndex], ...tableData };
            return { ...s, tables };
          }
          return s;
        });
        return { ...prev, framework: fw };
      });
    } catch { toast.error('Failed to update table'); }
  };

  const handleRemoveSectionTable = async (sectionId, tableIndex) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/remove-table/${tableIndex}`, { method: 'DELETE' });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) {
            const tables = [...(s.tables || [])];
            tables.splice(tableIndex, 1);
            return { ...s, tables };
          }
          return s;
        });
        return { ...prev, framework: fw };
      });
      toast.success('Table removed');
    } catch { toast.error('Failed to remove table'); }
  };

  // Section charts (DeskeyChart)
  const handleAddSectionChart = async (sectionId, chartData) => {
    try {
      const res = await api(`/studio/framework/${draft.id}/section/${sectionId}/add-chart`, {
        method: 'POST', body: JSON.stringify(chartData)
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, charts: [...(s.charts || []), res.chart] } : s);
        return { ...prev, framework: fw };
      });
      toast.success('Chart saved to section');
    } catch { toast.error('Failed to save chart'); }
  };
  const handleUpdateSectionChart = async (sectionId, chartIndex, chartData) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/update-chart/${chartIndex}`, {
        method: 'PUT', body: JSON.stringify(chartData)
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) {
            const charts = [...(s.charts || [])];
            charts[chartIndex] = { ...charts[chartIndex], ...chartData, updated_at: new Date().toISOString() };
            return { ...s, charts };
          }
          return s;
        });
        return { ...prev, framework: fw };
      });
      toast.success('Chart updated');
    } catch { toast.error('Failed to update chart'); }
  };
  const handleRemoveSectionChart = async (sectionId, chartIndex) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/delete-chart/${chartIndex}`, { method: 'DELETE' });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => {
          if (s.id === sectionId) {
            const charts = [...(s.charts || [])];
            charts.splice(chartIndex, 1);
            return { ...s, charts };
          }
          return s;
        });
        return { ...prev, framework: fw };
      });
      toast.success('Chart removed');
    } catch { toast.error('Failed to remove chart'); }
  };

  // ── Subsection (subtitle) charts + drawings ─────────────────────────────
  // Same shape as section-level handlers but addressed by (sectionId, subIdx).
  // Mutates `framework.sections[s].subsections[subIdx].{charts,drawings}` so
  // the renderer can drop the visual directly under the subtitle's prose.
  const _mutSub = (sectionId, subIdx, mutate) => setDraft(prev => {
    const fw = { ...prev.framework };
    fw.sections = (fw.sections || []).map(s => {
      if (s.id !== sectionId) return s;
      const subs = [...(s.subsections || [])];
      if (!(subIdx >= 0 && subIdx < subs.length)) return s;
      subs[subIdx] = mutate({ ...(subs[subIdx] || {}) });
      return { ...s, subsections: subs };
    });
    return { ...prev, framework: fw };
  });

  const handleAddSubsectionChart = async (sectionId, subIdx, chartData) => {
    try {
      const res = await api(`/studio/framework/${draft.id}/section/${sectionId}/subsection/${subIdx}/add-chart`, {
        method: 'POST', body: JSON.stringify(chartData),
      });
      _mutSub(sectionId, subIdx, (sub) => ({ ...sub, charts: [...(sub.charts || []), res.chart] }));
      toast.success('Chart saved to subtitle');
    } catch { toast.error('Failed to save chart'); }
  };
  const handleUpdateSubsectionChart = async (sectionId, subIdx, chartIdx, chartData) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/subsection/${subIdx}/update-chart/${chartIdx}`, {
        method: 'PUT', body: JSON.stringify(chartData),
      });
      _mutSub(sectionId, subIdx, (sub) => {
        const charts = [...(sub.charts || [])];
        charts[chartIdx] = { ...charts[chartIdx], ...chartData, updated_at: new Date().toISOString() };
        return { ...sub, charts };
      });
      toast.success('Chart updated');
    } catch { toast.error('Failed to update chart'); }
  };
  const handleRemoveSubsectionChart = async (sectionId, subIdx, chartIdx) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/subsection/${subIdx}/delete-chart/${chartIdx}`, { method: 'DELETE' });
      _mutSub(sectionId, subIdx, (sub) => {
        const charts = [...(sub.charts || [])];
        charts.splice(chartIdx, 1);
        return { ...sub, charts };
      });
      toast.success('Chart removed');
    } catch { toast.error('Failed to remove chart'); }
  };
  const handleAttachSubsectionDrawing = async (sectionId, subIdx, dataUrl) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/subsection/${subIdx}/attach-drawing`, {
        method: 'POST', body: JSON.stringify({ image_url: dataUrl, caption: 'Drawing' }),
      });
      _mutSub(sectionId, subIdx, (sub) => ({
        ...sub,
        drawings: [...(sub.drawings || []), { url: dataUrl, caption: 'Drawing', created_at: new Date().toISOString() }],
      }));
      toast.success('Drawing saved to subtitle');
    } catch { toast.error('Failed to save drawing'); }
  };
  const handleRemoveSubsectionDrawing = async (sectionId, subIdx, imgIdx) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/subsection/${subIdx}/delete-drawing/${imgIdx}`, { method: 'DELETE' });
      _mutSub(sectionId, subIdx, (sub) => {
        const drawings = [...(sub.drawings || [])];
        drawings.splice(imgIdx, 1);
        return { ...sub, drawings };
      });
      toast.success('Drawing removed');
    } catch { toast.error('Failed to remove drawing'); }
  };

  // Section notes handler
  const handleSaveNotes = async (sectionId, notes) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/notes`, {
        method: 'PUT', body: JSON.stringify({ notes })
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, teacher_notes: notes } : s);
        return { ...prev, framework: fw };
      });
    } catch { /* silent autosave */ }
  };

  // Section attachments (file uploads)
  const handleUploadSectionAttachment = async (sectionId, file, title, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title || file.name.replace(/\.[^/.]+$/, ''));
    try {
      const res = await apiUpload(`/studio/framework/${draft.id}/section/${sectionId}/upload-attachment`, fd);
      if (res.detail) throw new Error(res.detail);
      if (!res.added) throw new Error('Upload failed');
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, attachments: [...(s.attachments || []), res.attachment] } : s);
        return { ...prev, framework: fw };
      });
      toast.success(`Attached: ${res.attachment.title}`);
      return res.attachment;
    } catch (e) {
      toast.error(e.message || 'Upload failed');
      throw e;
    }
  };
  // `extra` carries a width/order resize patch alongside the (always-
  // required) title — lets the same endpoint back both the rename UI and
  // the attachment card's resize control.
  const handleRenameSectionAttachment = async (sectionId, attachmentId, title, extra = {}) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/rename-attachment/${attachmentId}`, {
        method: 'PUT', body: JSON.stringify({ title, ...extra })
      });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, attachments: (s.attachments || []).map(a => a.id === attachmentId ? { ...a, title, ...extra } : a) } : s);
        return { ...prev, framework: fw };
      });
      if (!extra.width) toast.success('Renamed');
    } catch { toast.error('Rename failed'); }
  };
  const handleRemoveSectionAttachment = async (sectionId, attachmentId) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}/delete-attachment/${attachmentId}`, { method: 'DELETE' });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, attachments: (s.attachments || []).filter(a => a.id !== attachmentId) } : s);
        return { ...prev, framework: fw };
      });
      toast.success('Attachment removed');
    } catch { toast.error('Failed to remove'); }
  };

  // Remove an entry from the draft-level Attachments panel (draft.references)
  const handleRemoveReference = async (refIndex) => {
    try {
      await api(`/studio/drafts/${draft.id}/references/${refIndex}`, { method: 'DELETE' });
      setDraft(prev => {
        const refs = [...(prev.references || [])];
        refs.splice(refIndex, 1);
        return { ...prev, references: refs };
      });
      toast.success('Attachment removed');
    } catch { toast.error('Failed to remove attachment'); }
  };

  // Inline section save
  const saveSection = async (sectionId) => {
    try {
      await api(`/studio/framework/${draft.id}/section/${sectionId}`, { method: 'PUT', body: JSON.stringify({ content: editContent }) });
      setDraft(prev => {
        const fw = { ...prev.framework };
        fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, content: editContent } : s);
        return { ...prev, framework: fw };
      });
      setEditingSection(null);
      toast.success('Saved');
    } catch { toast.error('Failed'); }
  };

  // Image generation
  const [imageGenSection, setImageGenSection] = useState(null); // section awaiting image gen
  const [imageGenPrompt, setImageGenPrompt] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);
  const [addSectionMode, setAddSectionMode] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '' | 'saving' | 'saved'
  const autoSaveTimer = useRef(null); // 'custom' | 'blackboard'
  const [customTitle, setCustomTitle] = useState('');
  const [customType, setCustomType] = useState('text');
  const [addingSec, setAddingSec] = useState(false);
  const [bbFile, setBbFile] = useState(null);
  const [bbAnalyzing, setBbAnalyzing] = useState(false);
  const [bbType, setBbType] = useState('blackboard_analysis');

  // Undo / Redo — directly fetch updated draft and sync local state
  const handleUndo = async () => {
    if (!draft?.id) return;
    const res = await api(`/studio/drafts/${draft.id}/undo`, { method: 'POST' });
    if (res.undone && res.framework) {
      setDraft(prev => ({ ...prev, framework: res.framework }));
      setCanRedo(true);
      toast.success('Undone');
    } else if (res.undone) {
      // Fallback: re-fetch the full draft
      const updated = await api(`/studio/drafts/${draft.id}`);
      if (updated?.id) setDraft(updated);
      setCanRedo(true);
      toast.success('Undone');
    } else {
      toast.info('Nothing to undo');
    }
  };

  const handleRedo = async () => {
    if (!draft?.id) return;
    const res = await api(`/studio/drafts/${draft.id}/redo`, { method: 'POST' });
    if (res.redone && res.framework) {
      setDraft(prev => ({ ...prev, framework: res.framework }));
      setCanUndo(true);
      toast.success('Redone');
    } else if (res.redone) {
      const updated = await api(`/studio/drafts/${draft.id}`);
      if (updated?.id) setDraft(updated);
      setCanUndo(true);
      toast.success('Redone');
    } else {
      toast.info('Nothing to redo');
    }
  };

  // Auto-save on framework changes
  const triggerAutosave = useCallback(() => {
    if (!draft?.id) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      setAutoSaveStatus('saving');
      await api(`/studio/drafts/${draft.id}/autosave`, { method: 'PUT' }).catch(() => {});
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
    }, 3000);
  }, [draft?.id]);

  // Keyboard shortcut: Ctrl+Z / Ctrl+Shift+Z
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); handleRedo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const handleGenerateImage = async (sectionId, customPrompt) => {
    // If no custom prompt yet, trigger interactive flow in chat
    if (!customPrompt && !imageGenSection) {
      const sec = sections.find(s => s.id === sectionId);
      setImageGenSection(sec);
      // The interactive image gen card will be shown in the chat panel via imageGenSection state
      return;
    }

    // Actually generate
    setProcessing(`img-${sectionId}`);
    setImageGenSection(null);
    try {
      const body = { draft_id: draft.id, section_id: sectionId };
      if (customPrompt) body.prompt = customPrompt;
      const res = await api('/studio/generate-image', { method: 'POST', body: JSON.stringify(body) });
      if (res.image) {
        setDraft(prev => {
          const fw = { ...prev.framework };
          fw.sections = (fw.sections || []).map(s => s.id === sectionId ? { ...s, generated_images: [...(s.generated_images || []), { url: res.image, prompt: customPrompt || 'auto' }] } : s);
          return { ...prev, framework: fw };
        });
        toast.success('Image generated!');
        // Add success message to chat history
        setDraft(prev => ({
          ...prev,
          chat_history: [...(prev.chat_history || []), {
            role: 'assistant',
            message: `Image generated for "${sections.find(s => s.id === sectionId)?.title || 'section'}"! Check the section to see it.`,
            timestamp: new Date().toISOString()
          }]
        }));
      }
    } catch { toast.error('Image generation failed'); }
    setProcessing('');
  };

  // Publish
  const handlePublish = async () => {
    // Deskey Session drafts (from the audio inbox) always publish via the
    // chapter/topic picker — even if the class was cleared — so the teacher can
    // (re)pick class + chapter there instead of the generic multi-class flow.
    if (draft?.class_id || draft?.audio_inbox_id) {
      setShowLessonPublish(true);
      return;
    }
    if (!selectedClasses.length) { toast.error('Select a class'); return; }
    setProcessing('publish');
    try {
      const res = await api('/studio/publish', { method: 'POST', body: JSON.stringify({ draft_id: draft.id, class_ids: selectedClasses }) });
      if (res.published) { toast.success(`Published to ${res.class_count} class(es)`); setDraft(prev => ({ ...prev, status: 'published' })); setShowPublish(false); reload(); }
    } catch { toast.error('Failed'); }
    setProcessing('');
  };

  const handleLessonPublished = (res) => {
    setDraft(prev => ({ ...prev, status: 'published', lesson_id: res.lesson_id, topic_id: res.topic_id }));
    setShowPublish(false);
    setShowLessonPublish(false);
    reload && reload();
  };

  const totalPts = sections.filter(s => s.type === 'problems').reduce((a, s) => a + (s.items || []).reduce((b, i) => b + (i.points || 0), 0), 0);

  // Rename the session inline on the header card and persist it.
  const saveTitle = async () => {
    setTitleEditMode(false);
    const t = (draft.title || '').trim() || 'Deskey Session';
    if (t !== draft.title) setDraft(prev => ({ ...prev, title: t }));
    await api(`/studio/drafts/${draft.id}`, { method: 'PUT', body: JSON.stringify({ title: t }) }).catch(() => {});
  };
  const sessionClassName = classMeta?.name || (classes || []).find(c => c.id === draft?.class_id)?.name || '';

  return (
    <div className="flex h-full gap-0 bg-white" data-testid="content-editor">
      <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-file-input" />
      <input ref={blackboardInputRef} type="file" accept="image/*" onChange={handleBlackboardUpload} className="hidden" data-testid="blackboard-file-input" />
      <input ref={chatImageInputRef} type="file" accept="image/*" onChange={handleChatAttachmentUpload} className="hidden" data-testid="chat-image-file-input" />

      {/* Left: Content Editor */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        {/* Toolbar — glassmorphism · pro-level */}
        <div className="h-[60px] flex items-center px-6 gap-3 flex-shrink-0 sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-100" style={{ boxShadow: '0 1px 0 rgba(15,23,42,0.04)' }}>
          <button onClick={goBack} className="p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200 active:scale-95" data-testid="back-btn"><X className="w-4 h-4" /></button>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {/* Read-only here — the title is renamed inline on the session card below */}
            <input
              value={draft.title}
              readOnly
              tabIndex={-1}
              className="text-[16px] font-extrabold text-slate-900 bg-transparent border-none outline-none flex-1 min-w-0 cursor-default"
              style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}
              data-testid="draft-title-input"
              title={draft.title}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-6 w-px bg-slate-200 mx-1" />
            <button onClick={() => photoInputRef.current?.click()} className="p-2 rounded-xl text-slate-500 hover:text-teal-600 hover:bg-teal-50 transition-all duration-200 active:scale-95" title="Upload Photo" data-testid="upload-photo-btn"><Image className="w-4 h-4" /></button>
            {/* Reference (YouTube / Wikipedia URL) popover — relocated from the
                chat panel per spec. Click to expand the URL input + Attach btn
                immediately under the toolbar so the chat panel stays clean. */}
            <div className="relative" data-testid="reference-url-popover-wrap">
              <button
                onClick={() => setRefUrlOpen(o => !o)}
                className={`p-2 rounded-xl transition-all duration-200 active:scale-95 ${refUrlOpen ? 'text-teal-700 bg-teal-50' : 'text-slate-500 hover:text-teal-600 hover:bg-teal-50'}`}
                title="Reference — YouTube or Wikipedia URL"
                data-testid="reference-url-toolbar-btn">
                <Link2 className="w-4 h-4" />
              </button>
              {refUrlOpen && (
                <div
                  className="absolute z-[80] top-full left-0 mt-2 w-[320px] max-w-[calc(100vw-1rem)] p-3 rounded-xl bg-white shadow-2xl border border-slate-200"
                  data-testid="reference-url-popover">
                  <p className="text-[9px] font-semibold text-[#64748b] uppercase tracking-[0.08em] mb-2">Reference</p>
                  <div className="flex gap-2">
                    <Input
                      value={urlInput}
                      onChange={e => setUrlInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { handleExtractUrl(); setRefUrlOpen(false); } else if (e.key === 'Escape') { setRefUrlOpen(false); } }}
                      placeholder="YouTube or Wikipedia URL..."
                      autoFocus
                      className="h-9 text-[12px] flex-1 rounded-xl bg-white border-zinc-200 px-4 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 transition-all"
                      data-testid="url-input"
                    />
                    <Button
                      onClick={() => { handleExtractUrl(); setRefUrlOpen(false); }}
                      disabled={processing === 'extract' || !urlInput.trim()}
                      size="sm"
                      className="h-9 w-9 p-0 rounded-xl text-white shadow-sm transition-all duration-200 active:scale-95 bg-gradient-to-r from-[#34A853] to-[#0d9488]"
                      data-testid="extract-url-btn">
                      <Link2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2 leading-snug">
                    Paste a YouTube video or Wikipedia article URL — Deskey will pull a clean reference into your draft.
                  </p>
                </div>
              )}
            </div>
            {/* AI Reference Suggestions — auto-append related links via LLM. */}
            <button onClick={handleSuggestReferences} disabled={processing === 'refs'} className="p-2 rounded-xl text-slate-500 hover:text-teal-600 hover:bg-teal-50 transition-all duration-200 active:scale-95 disabled:opacity-50" title="AI Reference Suggestions" data-testid="suggest-refs-btn"><Wand2 className="w-4 h-4" /></button>
            <div className="h-6 w-px bg-slate-200 mx-1" />
            <button onClick={() => {
              const firstSec = framework?.sections?.[0];
              setImageHelperModal({ sectionId: targetSection?.id || firstSec?.id || null });
            }} className="px-3 h-9 rounded-xl text-[12px] font-bold text-white bg-gradient-to-r from-[#34A853] to-[#0d9488] hover:shadow-[0_6px_16px_rgba(52,168,83,0.35)] shadow-[0_3px_10px_rgba(52,168,83,0.25)] transition-all duration-200 active:scale-95 flex items-center gap-1.5" title="AI Image — Nano Banana Flash" data-testid="open-image-helper">
              <Sparkles className="w-3.5 h-3.5" /> <span className="hidden lg:inline">Generate Image</span>
            </button>
            {(framework || draft.content) && (
              <>
                {/* Undo / Redo / Autosave — always visible, even once the
                    lesson is published, so a teacher touching up a live
                    lesson can still step back through their edits. Icon
                    color itself signals availability: dark when there's
                    something to undo/redo, light gray (and disabled) when
                    there isn't — no need to click-and-see-a-toast to find out. */}
                <div className="flex items-center gap-1 ml-2 mr-1">
                  <button onClick={handleUndo} disabled={!canUndo} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:cursor-not-allowed ${canUndo ? 'text-slate-800 hover:bg-gray-100' : 'text-gray-300'}`} title="Undo (Ctrl+Z)" data-testid="undo-btn"><Undo2 className="w-4 h-4" /></button>
                  <button onClick={handleRedo} disabled={!canRedo} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:cursor-not-allowed ${canRedo ? 'text-slate-800 hover:bg-gray-100' : 'text-gray-300'}`} title="Redo (Ctrl+Shift+Z)" data-testid="redo-btn"><Redo2 className="w-4 h-4" /></button>
                  {autoSaveStatus && (
                    <span className={`text-[10px] font-medium ml-1 ${autoSaveStatus === 'saving' ? 'text-amber-500' : 'text-green-500'}`} data-testid="autosave-status">
                      {autoSaveStatus === 'saving' ? 'Saving...' : 'Saved'}
                    </span>
                  )}
                </div>
                <Button onClick={() => setShowPreview(true)} size="sm" variant="outline" className="h-9 px-5 rounded-xl text-xs font-medium ml-1 border-zinc-200 text-[#475569] hover:text-[#34A853] hover:border-[#34A853]/30 hover:bg-[#34A853]/5 transition-all duration-200 active:scale-95" data-testid="preview-btn"><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview</Button>
                {draft.status !== 'published' ? (
                  <Button onClick={() => { if (draft?.class_id || draft?.audio_inbox_id) setShowLessonPublish(true); else setShowPublish(true); }} size="sm" className="h-9 px-5 rounded-xl text-white text-xs font-semibold shadow-md transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-95 bg-gradient-to-r from-[#34A853] via-[#0d9488] to-[#0d9488] ring-1 ring-[#34A853]/40 ring-offset-1" data-testid="publish-btn" title="Publish this lesson to your students' Student Desk">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Publish to Student Desk
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => { if (draft?.class_id || draft?.audio_inbox_id) setShowLessonPublish(true); else setShowPublish(true); }} size="sm" className="h-9 px-5 rounded-xl text-white text-xs font-semibold shadow-md transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-95 bg-gradient-to-r from-[#0d9488] via-[#0d9488] to-[#34A853] ring-1 ring-[#34A853]/40 ring-offset-1" data-testid="republish-btn" title="Re-send the updated lesson to your students' Student Desk">
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Update Student Desk
                    </Button>
                    <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[10px] font-bold tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200" title="This lesson is live on your students' dashboards" data-testid="published-badge">
                      <CheckCircle className="w-3 h-3" /> Live on Student Desk
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto bg-[#f5f8f7]">
          {!draft.transcript && !framework && (
            <div className="p-6 flex items-center justify-center min-h-[500px]">
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center max-w-lg" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-50 to-green-100 flex items-center justify-center mx-auto mb-5"><Mic className="w-10 h-10 text-emerald-500" /></div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Start with Transcription</h3>
                <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto leading-relaxed">Transcribe your recording to generate a comprehensive AI-powered lesson framework</p>
                <Button onClick={handleTranscribe} disabled={processing === 'transcribe'} className="h-12 px-8 rounded-xl text-white text-sm font-bold" style={{ background: G }} data-testid="transcribe-btn">
                  <Mic className="w-4 h-4 mr-2" />{processing === 'transcribe' ? 'Transcribing...' : 'Transcribe Recording'}
                </Button>
              </div>
            </div>
          )}

          {draft.transcript && !framework && (
            <div className="p-6 space-y-5">
              <div className="bg-white rounded-xl border border-gray-200 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-3"><Mic className="w-3.5 h-3.5" /> Transcript</h4>
                <div className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-y-auto rounded-lg bg-gray-50/50 p-3" data-testid="transcript-content">{draft.transcript}</div>
              </div>
              <div className="bg-[#34A853]/5 rounded-2xl border border-[#34A853]/15 p-8 text-center" style={{ boxShadow: '0 2px 12px rgba(52,168,83,0.06)' }}>
                <div className="w-16 h-16 rounded-2xl bg-white/80 flex items-center justify-center mx-auto mb-4 shadow-sm"><Brain className="w-8 h-8 text-indigo-500" /></div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Generate Lesson Framework</h3>
                <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto leading-relaxed">AI will create a structured lesson tailored to the subject &amp; grade level you choose below.</p>
                {/* Classifier pills */}
                {taxonomy && (
                  <div className="max-w-2xl mx-auto mb-5 text-left bg-white rounded-xl border border-indigo-100 p-4 space-y-3" data-testid="classifier-panel">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Auto-Detected {classifying ? '(analysing…)' : classification?.method === 'ai' ? '(AI)' : classification?.method === 'override' ? '(manual)' : ''}</span>
                      {classification && <span className="text-[10px] text-gray-400 ml-auto">Click a pill below to override</span>}
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Subject</p>
                      <div className="flex flex-wrap gap-1.5">
                        {taxonomy.subjects.map(s => {
                          const active = (overrideSubject || classification?.subject) === s.id;
                          return (
                            <button key={s.id} onClick={() => setOverrideSubject(s.id)}
                              className={`text-[11px] font-semibold px-3 py-1.5 rounded-full transition-all ${active ? 'bg-indigo-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600'}`}
                              data-testid={`classifier-subject-${s.id}`}>{s.label}</button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Grade Level</p>
                      <div className="flex flex-wrap gap-1.5">
                        {taxonomy.grade_bands.map(g => {
                          const active = (overrideGrade || classification?.grade_band) === g.id;
                          return (
                            <button key={g.id} onClick={() => setOverrideGrade(g.id)}
                              className={`text-[11px] font-semibold px-3 py-1.5 rounded-full transition-all ${active ? 'bg-[#34A853] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-[#34A853]/10 hover:text-[#34A853]'}`}
                              data-testid={`classifier-grade-${g.id}`}>{g.label}</button>
                          );
                        })}
                      </div>
                    </div>
                    {sectionPlan?.core_sections?.length > 0 && (
                      <details className="mt-2" data-testid="classifier-plan-details">
                        <summary className="text-[11px] font-semibold text-indigo-600 cursor-pointer hover:underline">Preview {sectionPlan.core_sections.length} sections that will be generated →</summary>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {sectionPlan.core_sections.map((s, i) => (
                            <span key={s.id || i} className="text-[10px] font-medium bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{s.title}</span>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                <Button onClick={handleGenerateFramework} disabled={processing === 'framework'} className="h-12 px-8 rounded-xl text-white text-sm font-bold shadow-md" style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }} data-testid="generate-framework-btn">
                  <Sparkles className="w-4 h-4 mr-2" />{processing === 'framework' ? 'Generating...' : 'Generate AI Framework'}
                </Button>
              </div>
            </div>
          )}

          {framework && (
            <div className="p-6 space-y-5">
              {/* Auto-Sync Multimodal Smart Note — voice ↔ board fusion panel.
                  Renders only if the draft was generated from /auto-sync/.../save-to-draft */}
              <SmartNoteBoardPanel draft={draft} />
              <div className="relative bg-white rounded-3xl p-6 border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 0 rgba(15,23,42,0.04), 0 12px 40px rgba(15,23,42,0.06)' }}>
                {/* Premium gradient ribbon at top */}
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #34A853 0%, #0d9488 50%, #6366f1 100%)' }} />
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 shadow-[0_8px_24px_rgba(13,148,136,0.28)]"><BookOpen className="w-6 h-6 text-white" /></div>
                  <div className="flex-1 min-w-0">
                    {titleEditMode ? (
                      <input
                        value={draft.title}
                        onChange={e => setDraft(prev => ({ ...prev, title: e.target.value }))}
                        onBlur={saveTitle}
                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        autoFocus
                        className="w-full text-[19px] font-extrabold text-slate-900 bg-emerald-50/40 rounded-md px-2 py-0.5 -mx-2 ring-1 ring-emerald-200 border-none outline-none"
                        style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.025em' }}
                        data-testid="session-title-input"
                        title="Press Enter to finish renaming"
                      />
                    ) : (
                      <div className="flex items-center gap-2 min-w-0 group/cardtitle">
                        <h2 className="text-[19px] font-extrabold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.025em' }}>{draft.title || framework.title}</h2>
                        <button
                          type="button"
                          onClick={() => setTitleEditMode(true)}
                          data-testid="edit-title-btn"
                          title="Rename session"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex-shrink-0">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {/* Session fields — Department, Batch, Class, Subject, Chapter.
                        Dept/batch/subject follow the Settings toggles; class & chapter always show. */}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {cardFields.department && classMeta?.department && <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">{classMeta.department}</span>}
                      {cardFields.batch && classMeta?.batch && <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">{classMeta.batch}</span>}
                      {sessionClassName
                        ? <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{sessionClassName}</span>
                        : <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">No class</span>}
                      {cardFields.subject && classMeta?.subject && <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{classMeta.subject}</span>}
                      {chapterName
                        ? <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100">{chapterName}</span>
                        : <span className="text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">No chapter</span>}
                    </div>
                  </div>
                </div>
              </div>

              {sections.map((sec, idx) => (
                <FrameworkSection key={sec.id} section={sec} index={idx} total={sections.length} expanded={expandedSections[sec.id]} toggle={() => toggleSection(sec.id)}
                  editingSection={editingSection} setEditingSection={setEditingSection} editContent={editContent} setEditContent={setEditContent}
                  saveSection={() => saveSection(sec.id)} onGenerateImage={() => handleGenerateImage(sec.id)} isGeneratingImage={processing === `img-${sec.id}`}
                  onTargetChat={() => { setTargetSection(sec); setChatInput(`@${sec.title} `); chatRef.current?.focus(); }}
                  onOpenEquation={() => { setTargetSection(sec); setEquationModal(true); }}
                  onOpenImageHelper={() => { setTargetSection(sec); setImageHelperModal({ sectionId: sec.id }); }}
                  onDeleteEquation={(eqIdx) => {
                    setDraft(prev => {
                      const fw = { ...(prev.framework || {}) };
                      fw.sections = (fw.sections || []).map(s => {
                        if (s.id !== sec.id) return s;
                        const eqs = (s.equations || []).filter((_, i) => i !== eqIdx);
                        return { ...s, equations: eqs };
                      });
                      return { ...prev, framework: fw };
                    });
                    api(`/studio/framework/${draft.id}/section/${sec.id}`, {
                      method: 'PUT',
                      body: JSON.stringify({ equations: (sec.equations || []).filter((_, i) => i !== eqIdx) }),
                    }).catch(() => {});
                    triggerAutosave?.();
                  }}
                  draftId={draft.id}
                  onAddItem={(item) => handleAddItem(sec.id, item)} onDeleteItem={(idx) => handleDeleteItem(sec.id, idx)}
                  onUpdateItem={(idx, item) => handleUpdateItem(sec.id, idx, item)}
                  onOpenVisualModal={() => setVisualModal(sec)} onDeleteSection={() => handleDeleteSection(sec.id)}
                  onRemoveSectionImage={(imgIdx) => handleRemoveSectionImage(sec.id, imgIdx)}
                  onUpdateSectionImage={(imgIdx, patch) => handleUpdateSectionImage(sec.id, imgIdx, patch)}
                  onRemoveGeneratedImage={(imgIdx) => handleRemoveGeneratedImage(sec.id, imgIdx)}
                  onAddSectionLink={(linkData) => handleAddSectionLink(sec.id, linkData)}
                  onRemoveSectionLink={(linkIdx) => handleRemoveSectionLink(sec.id, linkIdx)}
                  onAddTable={(tableData) => handleAddSectionTable(sec.id, tableData)}
                  onUpdateTable={(tIdx, tData) => handleUpdateSectionTable(sec.id, tIdx, tData)}
                  onRemoveTable={(tIdx) => handleRemoveSectionTable(sec.id, tIdx)}
                  onSaveNotes={(notes) => handleSaveNotes(sec.id, notes)}
                  onAddChart={(chartData) => handleAddSectionChart(sec.id, chartData)}
                  onUpdateChart={(cIdx, cData) => handleUpdateSectionChart(sec.id, cIdx, cData)}
                  onRemoveChart={(cIdx) => handleRemoveSectionChart(sec.id, cIdx)}
                  onUploadAttachment={(file, title) => handleUploadSectionAttachment(sec.id, file, title)}
                  onRenameAttachment={(aid, title) => handleRenameSectionAttachment(sec.id, aid, title)}
                  onResizeAttachment={(aid, currentTitle, width) => handleRenameSectionAttachment(sec.id, aid, currentTitle, { width })}
                  onRemoveAttachment={(aid) => handleRemoveSectionAttachment(sec.id, aid)}
                  onAttachDrawing={(dataUrl) => {
                    api(`/studio/framework/${draft.id}/section/${sec.id}/attach-image`, {
                      method: 'POST', body: JSON.stringify({ image_url: dataUrl, caption: 'Drawing' })
                    }).then(() => {
                      setDraft(prev => {
                        const fw = { ...prev.framework };
                        fw.sections = (fw.sections || []).map(s => s.id === sec.id ? { ...s, section_images: [...(s.section_images || []), { url: dataUrl, caption: 'Drawing', created_at: new Date().toISOString() }] } : s);
                        return { ...prev, framework: fw };
                      });
                    }).catch(() => {});
                  }}
                  // Subsection (subtitle) chart + drawing handlers — bubble up
                  // so the renderer can drop visuals immediately under each
                  // subtitle's prose.
                  references={draft.references || []}
                  onAddSubChart={(subIdx, chartData) => handleAddSubsectionChart(sec.id, subIdx, chartData)}
                  onUpdateSubChart={(subIdx, chartIdx, chartData) => handleUpdateSubsectionChart(sec.id, subIdx, chartIdx, chartData)}
                  onRemoveSubChart={(subIdx, chartIdx) => handleRemoveSubsectionChart(sec.id, subIdx, chartIdx)}
                  onAttachSubDrawing={(subIdx, dataUrl) => handleAttachSubsectionDrawing(sec.id, subIdx, dataUrl)}
                  onRemoveSubDrawing={(subIdx, imgIdx) => handleRemoveSubsectionDrawing(sec.id, subIdx, imgIdx)} />
              ))}

              {/* + Add Section — stays available after publish so teachers can
                  update a live session; edits reflect to students on save. */}
              {(
                <div className="mt-4" data-testid="add-section-area">
                  {!showAddSection ? (
                    <button onClick={() => setShowAddSection(true)} className="w-full py-4 rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#34A853]/40 text-gray-400 hover:text-[#34A853] flex items-center justify-center gap-2 text-sm font-medium transition-all hover:bg-[#34A853]/[0.02] active:scale-[0.99]" data-testid="add-section-btn">
                      <Plus className="w-5 h-5" /> Add Section
                    </button>
                  ) : !addSectionMode ? (
                    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4 shadow-sm" data-testid="add-section-picker">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-bold text-gray-900">Add New Section</h4>
                        <button onClick={() => { setShowAddSection(false); setAddSectionMode(null); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                      </div>
                      {/* Adaptive Suggested Sections — from classifier + grade */}
                      {((sectionPlan?.suggested_sections || framework?.suggested_sections) || []).length > 0 && (
                        <div data-testid="add-suggested-sections">
                          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Recommended for {framework?.subject_label || sectionPlan?.subject_label || 'this lesson'}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {(sectionPlan?.suggested_sections || framework?.suggested_sections || []).slice(0, 8).map((s, i) => (
                              <button key={`${s.id}-${i}`} onClick={async () => {
                                setAddingSec(true);
                                const fd = new FormData();
                                fd.append('title', s.title);
                                fd.append('content', '');
                                fd.append('section_type', s.type || 'text');
                                try {
                                  const res = await fetch(`${API}/api/teacher/drafts/${draft.id}/add-section`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                                  const d = await res.json();
                                  if (d.section) { applyAddedSection(d.section); toast.success(`Added: ${s.title}`); reload(); setShowAddSection(false); }
                                  else toast.error(d.detail || 'Failed');
                                } catch { toast.error('Failed'); }
                                setAddingSec(false);
                              }} className="p-3 rounded-xl border border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all text-left flex items-start gap-2.5 group" data-testid={`suggested-section-${s.id}`}>
                                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-100 transition-colors">
                                  <Plus className="w-4 h-4 text-indigo-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[12px] font-bold text-gray-800 truncate">{s.title}</p>
                                  <p className="text-[10px] text-gray-400 uppercase mt-0.5 truncate">{s.type || 'text'}{s.from_domain ? ` · cross-domain` : ''}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                          <div className="h-px bg-gray-100 my-3" />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => setAddSectionMode('custom')} className="p-4 rounded-xl border border-gray-200 hover:border-[#34A853]/40 hover:bg-[#34A853]/[0.02] transition-all text-left" data-testid="add-custom-section">
                          <div className="w-10 h-10 rounded-xl bg-[#34A853]/10 flex items-center justify-center mb-3"><FileText className="w-5 h-5 text-[#34A853]" /></div>
                          <p className="text-sm font-bold text-gray-900">Custom Content</p>
                          <p className="text-xs text-gray-500 mt-1">Create an empty section to fill in or build with AI</p>
                        </button>
                        <button onClick={() => setAddSectionMode('blackboard')} className="p-4 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all text-left" data-testid="add-blackboard-section">
                          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3"><Camera className="w-5 h-5 text-amber-600" /></div>
                          <p className="text-sm font-bold text-gray-900">Blackboard Scan</p>
                          <p className="text-xs text-gray-500 mt-1">Upload a photo, AI digitizes it</p>
                        </button>
                      </div>
                    </div>
                  ) : addSectionMode === 'custom' ? (
                    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4 shadow-sm" data-testid="custom-section-form">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setAddSectionMode(null)} className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-4 h-4" /></button>
                          <h4 className="text-sm font-bold text-gray-900">Custom Section</h4>
                        </div>
                        <button onClick={() => { setShowAddSection(false); setAddSectionMode(null); setCustomTitle(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Title *</label>
                        <Input value={customTitle} onChange={e => setCustomTitle(e.target.value)} placeholder="Section title..." className="h-10 rounded-xl text-sm" data-testid="custom-section-title" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Type</label>
                        <div className="flex gap-2 flex-wrap">
                          {[{id:'text',label:'Text'},{id:'rich_text',label:'Rich Text'},{id:'cards',label:'Cards'},{id:'flashcards',label:'Flashcards'},{id:'problems',label:'Problems'},{id:'visual',label:'Visual'}].map(t => (
                            <button key={t.id} onClick={() => setCustomType(t.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${customType === t.id ? 'bg-[#34A853]/10 border-[#34A853]/30 text-[#34A853]' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>{t.label}</button>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">The section is created empty. Fill it in directly, or use AI to build it from this session.</p>
                      <Button onClick={async () => {
                        if (!customTitle.trim()) { toast.error('Title required'); return; }
                        setAddingSec(true);
                        const fd = new FormData();
                        fd.append('title', customTitle.trim());
                        fd.append('content', '');
                        fd.append('section_type', customType);
                        try {
                          const res = await fetch(`${API}/api/teacher/drafts/${draft.id}/add-section`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                          const d = await res.json();
                          if (d.section) { applyAddedSection(d.section); toast.success('Section added!'); reload(); setShowAddSection(false); setAddSectionMode(null); setCustomTitle(''); }
                          else toast.error(d.detail || 'Failed');
                        } catch { toast.error('Failed'); }
                        setAddingSec(false);
                      }} disabled={addingSec || !customTitle.trim()} className="w-full h-10 rounded-xl text-white text-sm font-semibold active:scale-[0.98] disabled:opacity-50 bg-gradient-to-r from-[#34A853] to-[#0d9488]" data-testid="save-custom-section">
                        {addingSec ? 'Adding...' : 'Add Section'}
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-amber-200 p-5 space-y-4 shadow-sm" data-testid="blackboard-form">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setAddSectionMode(null)} className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-4 h-4" /></button>
                          <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center"><Camera className="w-4 h-4 text-amber-600" /></div>
                          <h4 className="text-sm font-bold text-gray-900">Blackboard Scan</h4>
                        </div>
                        <button onClick={() => { setShowAddSection(false); setAddSectionMode(null); setBbFile(null); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">Upload a photo of a blackboard, whiteboard, or handwritten notes. AI will analyze the content, extract formulas, and create a complete digital section using this session's subject.</p>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Upload Photo *</label>
                        <div className={`relative rounded-xl border-2 border-dashed p-6 text-center transition-all ${bbFile ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 hover:border-amber-200'}`}>
                          <input type="file" accept="image/*" onChange={e => setBbFile(e.target.files?.[0] || null)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="bb-file-input" />
                          {bbFile ? (
                            <div className="flex items-center gap-3 justify-center">
                              <Camera className="w-5 h-5 text-amber-600" />
                              <div className="text-left"><p className="text-sm font-medium text-gray-900">{bbFile.name}</p><p className="text-xs text-gray-400">{(bbFile.size / 1024).toFixed(0)} KB</p></div>
                              <button onClick={e => { e.stopPropagation(); setBbFile(null); }} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <><Camera className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">Drop photo or click to upload</p><p className="text-[10px] text-gray-400 mt-1">JPG, PNG up to 10MB</p></>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">Section type</label>
                        <div className="flex gap-2 flex-wrap" data-testid="bb-type-selector">
                          {[{id:'text',label:'Text'},{id:'rich_text',label:'Rich Text'},{id:'cards',label:'Cards'},{id:'flashcards',label:'Flashcards'},{id:'problems',label:'Problems'},{id:'list',label:'List'},{id:'visual',label:'Visual'}].map(t => (
                            <button key={t.id} onClick={() => setBbType(t.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${bbType === t.id ? 'bg-amber-100 border-amber-300 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`} data-testid={`bb-type-${t.id}`}>{t.label}</button>
                          ))}                        </div>

                        <p className="text-[10px] text-gray-400 mt-1.5 leading-snug">{bbType === 'blackboard_analysis' ? 'Keeps the board content as an analysis section (extracted text, formulas, steps).' : 'AI builds a ' + bbType.replace('_', ' ') + ' section from the scanned board content.'}</p>
                      </div>
                      <Button onClick={async () => {
                        if (!bbFile) { toast.error('Upload a photo'); return; }
                        setBbAnalyzing(true);
                        const fd = new FormData();
                        fd.append('draft_id', draft.id);
                        fd.append('section_type', bbType);
                        fd.append('file', bbFile);
                        try {
                          const res = await fetch(`${API}/api/teacher/analyze-blackboard`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                          const d = await res.json();
                          if (d.section) { applyAddedSection(d.section); toast.success('Blackboard analyzed!'); reload(); setShowAddSection(false); setAddSectionMode(null); setBbFile(null); }
                          else toast.error(d.detail || 'Failed');
                        } catch { toast.error('Failed'); }
                        setBbAnalyzing(false);
                      }} disabled={bbAnalyzing || !bbFile} className="w-full h-10 rounded-xl text-white text-sm font-semibold active:scale-[0.98] disabled:opacity-50 bg-gradient-to-r from-amber-500 to-orange-500" data-testid="analyze-blackboard-btn">
                        {bbAnalyzing ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Analyzing with AI...</> : <><Camera className="w-4 h-4 mr-2" /> Analyze &amp; Create Section</>}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Blackboard Analysis Results */}
              {showBlackboardPanel && (
                <div className="bg-white rounded-xl border border-amber-200 overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(245,158,11,0.1)' }} data-testid="blackboard-panel">
                  <div className="flex items-center gap-3 p-4 bg-amber-50/50 border-b border-amber-100">
                    <Camera className="w-5 h-5 text-amber-600" />
                    <h4 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Blackboard Analysis</h4>
                    <button onClick={() => setShowBlackboardPanel(false)} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                  </div>
                  {processing === 'blackboard' && (
                    <div className="p-8 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-amber-100 flex items-center justify-center"><Camera className="w-6 h-6 text-amber-600 animate-pulse" /></div>
                      <p className="text-sm font-bold text-gray-700">Analyzing handwritten content...</p>
                      <p className="text-xs text-gray-400 mt-1">GPT-5.2 Vision is reading your blackboard</p>
                      <div className="mt-3 flex justify-center"><div className="flex gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" /><span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></div></div>
                    </div>
                  )}
                  {blackboardAnalysis && !processing && (
                    <div className="p-5 space-y-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{blackboardAnalysis.detected_type}</span>
                        {blackboardAnalysis.title && <span className="text-sm font-bold text-gray-900">{blackboardAnalysis.title}</span>}
                      </div>
                      {blackboardAnalysis.text_content && (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Extracted Text</p>
                          <p className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap">{blackboardAnalysis.text_content}</p>
                        </div>
                      )}
                      {blackboardAnalysis.formulas?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Formulas & Equations</p>
                          <div className="space-y-2">
                            {blackboardAnalysis.formulas.map((f, i) => (
                              <div key={i} className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                                <p className="text-sm font-mono font-bold text-indigo-900">{f.plain || f.latex}</p>
                                {f.description && <p className="text-xs text-indigo-600 mt-1">{f.description}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {blackboardAnalysis.equations?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Equations</p>
                          <div className="flex flex-wrap gap-2">
                            {blackboardAnalysis.equations.map((eq, i) => (
                              <div key={i} className="bg-cyan-50 rounded-lg px-3 py-2 border border-cyan-100">
                                <p className="text-sm font-mono font-bold text-cyan-900">{eq.expression}</p>
                                <p className="text-[9px] text-cyan-600 uppercase mt-0.5">{eq.type}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {blackboardAnalysis.diagrams?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Diagrams Detected</p>
                          {blackboardAnalysis.diagrams.map((d, i) => (
                            <div key={i} className="bg-purple-50 rounded-lg p-3 border border-purple-100 mb-2">
                              <p className="text-xs font-bold text-purple-900">{d.description}</p>
                              <span className="text-[9px] font-bold text-purple-500 uppercase">{d.type}</span>
                              {d.labels?.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{d.labels.map((l, j) => <span key={j} className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{l}</span>)}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {blackboardAnalysis.key_points?.length > 0 && (
                        <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100">
                          <p className="text-[10px] font-bold text-emerald-700 uppercase mb-1">Key Points</p>
                          {blackboardAnalysis.key_points.map((p, i) => <p key={i} className="text-xs text-emerald-800 flex items-start gap-1.5 mb-0.5"><CheckCircle className="w-3 h-3 mt-0.5 text-emerald-500 flex-shrink-0" />{p}</p>)}
                        </div>
                      )}
                      <button onClick={() => blackboardInputRef.current?.click()} className="text-xs font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1" data-testid="analyze-another-btn">
                        <Camera className="w-3.5 h-3.5" /> Analyze Another Blackboard
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* AI Reference Suggestions */}
              {showRefSuggestions && (
                <div ref={refSuggestionsRef} className="bg-white rounded-xl border border-indigo-200 overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(99,102,241,0.08)', scrollMarginTop: '72px' }} data-testid="ref-suggestions-panel">
                  <div className="flex items-center gap-3 p-4 bg-indigo-50/50 border-b border-indigo-100">
                    <Wand2 className="w-5 h-5 text-indigo-600" />
                    <h4 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Reference Suggestions</h4>
                    <button onClick={() => setShowRefSuggestions(false)} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                  </div>
                  {processing === 'refs' ? (
                    <div className="p-6 text-center">
                      <div className="flex justify-center mb-2"><div className="flex gap-1"><span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" /><span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></div></div>
                      <p className="text-xs text-gray-500">Finding relevant resources...</p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-2">
                      {refSuggestions.map((ref, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors group" data-testid={`ref-suggestion-${i}`}>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: ref.type === 'video' ? '#EF444418' : ref.type === 'interactive' ? '#8B5CF618' : '#3B82F618' }}>
                            {ref.type === 'video' ? <PlayCircle className="w-4 h-4 text-red-500" /> : ref.type === 'interactive' ? <Target className="w-4 h-4 text-purple-500" /> : <BookOpen className="w-4 h-4 text-blue-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <a href={refSuggestionUrl(ref)} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-gray-900 hover:text-indigo-600 hover:underline" data-testid={`ref-suggestion-link-${i}`}>{ref.title}</a>
                            <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">{ref.description}</p>
                            {ref.url_hint && (
                              <a href={refSuggestionUrl(ref)} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-500 hover:text-indigo-700 hover:underline mt-1 truncate flex items-center gap-1">
                                <Link2 className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{ref.url_hint}</span>
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${ref.relevance === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{ref.relevance}</span>
                            <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{ref.type}</span>
                            <button
                              onClick={() => handleAddSuggestedRef(ref, i)}
                              disabled={addingRefIdx === i}
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-white px-2 py-1 rounded-lg transition-all active:scale-95 disabled:opacity-50 hover:shadow-md"
                              style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}
                              title="Add to attachments"
                              data-testid={`ref-suggestion-add-${i}`}>
                              {addingRefIdx === i ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
                            </button>
                          </div>
                        </div>
                      ))}
                      <button onClick={handleSuggestReferences} className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 mt-2" data-testid="refresh-refs-btn">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh Suggestions
                      </button>
                    </div>
                  )}
                </div>
              )}

              {(draft.references || []).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><Link2 className="w-3.5 h-3.5" /> Attachments</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {(draft.references || []).map((ref, i) => ({ ref, i })).filter(({ ref }) => ref.type === 'photo').map(({ ref, i }) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-gray-100 group">
                        <img src={ref.url} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => handleRemoveReference(i)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                          title="Remove"
                          data-testid={`remove-reference-${i}`}
                        ><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 mt-2">
                    {(draft.references || []).map((ref, i) => ({ ref, i })).filter(({ ref }) => ref.type !== 'photo').map(({ ref, i }) => {
                      const isLink = ref.url && !ref.url.startsWith('lesson://');
                      return (
                        <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100 group">
                          {ref.thumbnail && <img src={ref.thumbnail} alt="" className="w-14 h-9 rounded object-cover flex-shrink-0" />}
                          {isLink ? (
                            <a href={ref.url} target="_blank" rel="noopener noreferrer"
                              className="flex-1 min-w-0 hover:underline" title={ref.url} data-testid={`open-reference-${i}`}>
                              <p className="text-xs font-bold text-gray-900 truncate">{ref.title}</p>
                            </a>
                          ) : (
                            <div className="flex-1 min-w-0"><p className="text-xs font-bold text-gray-900 truncate">{ref.title}</p></div>
                          )}
                          <span className="text-[9px] font-bold text-gray-400 uppercase bg-gray-100 px-1.5 py-0.5 rounded">{ref.type}</span>
                          <button
                            onClick={() => handleRemoveReference(i)}
                            className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
                            title="Remove"
                            data-testid={`remove-reference-${i}`}
                          ><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Divider: drag to resize editor ↔ AI chat */}
      <div
        onMouseDown={startChatResize}
        className="w-1.5 flex-shrink-0 cursor-col-resize relative group/divider hover:bg-emerald-100/60 active:bg-emerald-200/60 transition-colors"
        title="Drag to resize"
        data-testid="chat-resize-divider">
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-[#e2e8f0] group-hover/divider:bg-emerald-400 transition-colors" />
      </div>

      {/* Right: AI Chat — resizable via the divider */}
      <div className="flex-shrink-0 flex flex-col overflow-hidden bg-[#f5f8f7]" style={{ width: chatWidth }} data-testid="chat-panel">
        {/* Chat Header */}
        <div className="h-16 flex items-center px-6 gap-3 flex-shrink-0 backdrop-blur-xl bg-white/80 border-b border-[#e2e8f0]">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-r from-[#34A853] to-[#0d9488]">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-[15px] font-semibold text-[#1e293b]" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>AI Assistant</span>
          {targetSection && (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold pl-2.5 pr-1.5 py-1 rounded-full ml-auto bg-gradient-to-r from-[#34A853]/10 to-[#0d9488]/10 text-[#0d9488]" data-testid="chat-context-chip">
              Context: {targetSection.title}
              <button onClick={() => setTargetSection(null)} aria-label="Clear context" data-testid="chat-context-chip-clear"
                className="hover:bg-black/10 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center">
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          )}
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          {(draft.chat_history || []).length === 0 && (
            <div className="text-center py-10 px-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-gradient-to-r from-[#34A853]/10 to-[#0d9488]/10">
                <Sparkles className="w-7 h-7 text-[#0d9488]" />
              </div>
              <p className="text-xl font-semibold text-[#1e293b] mb-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>AI Content Editor</p>
              <p className="text-sm text-[#475569] max-w-[280px] mx-auto leading-relaxed mb-6">
                {classification?.subject
                  ? <>Subject-aware suggestions for <span className="font-semibold text-[#0d9488]">{classification.subject}</span> — tap one or use <span className="font-bold">@</span>section and <span className="font-bold">/</span>commands.</>
                  : <>Edit content, generate images, and refine your lessons with natural language</>}
              </p>
              <div className="flex flex-col gap-2.5 items-stretch text-left">
                {getSubjectSuggestions(classification?.subject, sections).map(sg => {
                  const Icon = sg.type === 'image' ? Image : sg.type === 'math' ? Sigma : sg.type === 'chem' ? FlaskConical : Edit;
                  const tint = sg.type === 'image' ? '#7c3aed'
                             : sg.type === 'math'  ? '#0d9488'
                             : sg.type === 'chem'  ? '#a855f7'
                             : '#64748b';
                  return (
                    <button key={sg.label} onClick={() => {
                      // These canned suggestions embed "@Title" as plain text —
                      // resolve it into a real context tag (same as picking from
                      // the @ menu) so Send doesn't fall through to conversation.
                      const m = sg.label.match(/^@(.+?)\s/);
                      if (m) {
                        const sec = m[1] === 'All Sections' ? ALL_SECTIONS_OPTION : sections.find(s => s.title === m[1]);
                        if (sec) setTargetSection(sec);
                      }
                      setChatInput(sg.label); chatRef.current?.focus();
                    }}
                      className="flex items-center gap-3 text-[12px] font-medium px-4 py-3 rounded-xl transition-all duration-200 bg-white border hover:shadow-[0_4px_24px_rgba(0,0,0,0.02)] hover:-translate-y-[1px] active:scale-[0.98]"
                      style={{ borderColor: `${tint}30` }}
                      data-testid={`suggest-${sg.type}`}>
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: tint }} />
                      <span style={{ color: tint }}>{sg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(draft.chat_history || []).map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} items-end gap-2.5`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mb-0.5 bg-gradient-to-r from-[#34A853] to-[#0d9488]">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className={`max-w-[82%] px-4 py-3 text-[13px] leading-relaxed rounded-2xl ${
                msg.role === 'user'
                  ? 'text-white rounded-br-lg bg-gradient-to-r from-[#34A853] to-[#0d9488] shadow-sm'
                  : 'bg-white text-[#475569] rounded-bl-lg border border-zinc-100 shadow-[0_4px_24px_rgba(0,0,0,0.02)]'
              }`}>
                {msg.role === 'user' && msg.target_all && (
                  <span className="text-white/70 text-[10px] font-semibold block mb-1">@All Sections</span>
                )}
                {msg.target_section && msg.role === 'user' && !msg.target_all && sections.find(s => s.id === msg.target_section)?.title && (
                  <span className="text-white/70 text-[10px] font-semibold block mb-1">@{sections.find(s => s.id === msg.target_section).title}</span>
                )}
                <RenderContent text={msg.message} markdown={msg.role === 'assistant'} className="text-[13px] leading-relaxed" />
                {msg.role === 'user' && (
                  (Array.isArray(msg.images) && msg.images.length > 0) ||
                  (Array.isArray(msg.references) && msg.references.length > 0)
                ) && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(msg.images || []).map((img, ii) => (
                      <span key={`img-${ii}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/15 text-white/90 truncate max-w-[160px]" title={img.title}>
                        {img.url
                          ? <img src={img.url} alt="" className="w-3.5 h-3.5 rounded-full object-cover flex-shrink-0" />
                          : <ImageIcon className="w-2.5 h-2.5 flex-shrink-0" />}
                        <span className="truncate">{img.title || 'Image'}</span>
                      </span>
                    ))}
                    {(msg.references || []).map((r, ri) => (
                      <span key={`ref-${ri}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/15 text-white/90 truncate max-w-[160px]" title={r.url || r.label}>
                        {r.kind === 'lesson' ? <BookMarked className="w-2.5 h-2.5 flex-shrink-0" /> : <Link2 className="w-2.5 h-2.5 flex-shrink-0" />}
                        <span className="truncate">{r.label}</span>
                      </span>
                    ))}
                  </div>
                )}
                {msg.role === 'assistant' && msg.model_used && (
                  <div className="mt-2 -mb-0.5">
                    <ModelBadge modelKey={msg.model_used} reason={msg.model_reason} testId={`chat-model-badge-${i}`} />
                  </div>
                )}
                {msg.role === 'assistant' && msg.message?.includes('Image generated') && (
                  <span className="flex items-center gap-1.5 mt-2 text-[10px] font-semibold text-[#0d9488]"><Image className="w-3 h-3" /> Image attached</span>
                )}
                {msg.role === 'assistant' && msg.response_type === 'confirm' && msg.confirm_payload && (
                  <div className="flex items-center gap-2 mt-2.5">
                    <button onClick={() => handleConfirmEdit(i, msg.confirm_payload)}
                      disabled={processing === 'chat'}
                      className="text-[11px] font-semibold px-3 py-1.5 rounded-full text-white bg-gradient-to-r from-[#34A853] to-[#0d9488] hover:shadow-md active:scale-95 transition-all disabled:opacity-50"
                      data-testid={`chat-confirm-btn-${i}`}>
                      Confirm
                    </button>
                  </div>
                )}
                {msg.role === 'assistant' && (msg.undoable || msg.checkpoint_base !== undefined) && (
                  <div className="mt-2.5 flex items-center gap-3">
                    {msg.undoable && (msg.undone ? (
                      <span className="text-[10px] font-semibold text-[#94a3b8]">Undone</span>
                    ) : (
                      <button onClick={() => handleUndoChatEdit(i, msg.edit_id)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#64748b] hover:text-[#0d9488] transition-colors"
                        data-testid={`chat-undo-btn-${i}`}>
                        <Undo2 className="w-3 h-3" /> Undo this change
                      </button>
                    ))}
                    {msg.checkpoint_base !== undefined && !['clarify', 'confirm'].includes(msg.response_type) && (
                      <button onClick={() => handleRerunTurn(i)} disabled={processing === 'chat'}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#64748b] hover:text-[#0d9488] transition-colors disabled:opacity-50"
                        title="Regenerate this response (new branch from this point)"
                        data-testid={`chat-regenerate-btn-${i}`}>
                        <RefreshCw className="w-3 h-3" /> Regenerate
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Interactive Image Gen Card — removed, now using main chat input below */}

          {processing === 'chat' && (
            <div className="flex justify-start items-end gap-2.5">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mb-0.5 bg-gradient-to-r from-[#34A853] to-[#0d9488]">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-lg text-[13px] flex items-center gap-3 border border-zinc-100 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full animate-bounce bg-[#34A853]" />
                  <span className="w-2 h-2 rounded-full animate-bounce bg-[#0d9488]" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full animate-bounce bg-[#34A853]" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-[#64748b] font-medium text-[12px]">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* URL Reference panel was relocated to the top toolbar
            (data-testid="reference-url-toolbar-btn") so the chat panel stays
            focused on the AI chat composer. */}

        {/* Chat Input */}
        <div className="px-6 py-5 bg-white border-t border-[#e2e8f0] relative">
          {/* @Mention Menu */}
          {showMentionMenu && filteredSections.length > 0 && (
            <div className="absolute bottom-full left-6 right-6 mb-2 bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.08)] max-h-[260px] overflow-y-auto z-50 border border-zinc-100" data-testid="mention-menu">
              <div className="px-4 py-2.5 border-b border-zinc-100"><p className="text-[9px] font-semibold text-[#64748b] uppercase tracking-[0.08em]">Target Section</p></div>
              {filteredSections.map((s, i) => {
                const SIcon = getSectionIcon(s.icon, s.title);
                return (
                  <button key={s.id} onClick={() => selectMention(s)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${i === menuIndex ? 'bg-gradient-to-r from-[#34A853]/5 to-[#0d9488]/5' : 'hover:bg-zinc-50'}`}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-r from-[#34A853]/10 to-[#0d9488]/10"><SIcon className="w-3.5 h-3.5 text-[#0d9488]" /></div>
                    <span className="text-[12px] font-semibold text-[#1e293b] truncate">{s.title}</span>
                    <span className="text-[9px] text-[#64748b] font-medium ml-auto uppercase tracking-wider">{s.type}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* /Command Menu */}
          {showCommandMenu && filteredCommands.length > 0 && (
            <div className="absolute bottom-full left-6 right-6 mb-2 bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.08)] max-h-[260px] overflow-y-auto z-50 border border-zinc-100" data-testid="command-menu">
              <div className="px-4 py-2.5 border-b border-zinc-100"><p className="text-[9px] font-semibold text-[#64748b] uppercase tracking-[0.08em]">Commands</p></div>
              {filteredCommands.map((c, i) => (
                <button key={c.cmd} onClick={() => selectCommand(c)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${i === menuIndex ? 'bg-gradient-to-r from-[#34A853]/5 to-[#0d9488]/5' : 'hover:bg-zinc-50'}`}>
                  <span className="text-[12px] font-mono font-semibold text-[#0d9488] flex-shrink-0">{c.cmd}</span>
                  <span className="text-[12px] text-[#1e293b] font-medium flex-shrink-0 whitespace-nowrap">{c.label}</span>
                  <span className="text-[9px] text-[#64748b] ml-auto truncate min-w-0">{c.desc}</span>
                </button>
              ))}
            </div>
          )}

          {/* Image Gen Mode — replaces normal chat input when active */}
          {imageGenSection ? (
            <div data-testid="image-gen-card">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#34A853] to-[#0d9488] flex-shrink-0">
                  <Image className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900">Generate Image</p>
                  <p className="text-[11px] text-gray-500 truncate">for &ldquo;{imageGenSection.title}&rdquo;</p>
                </div>
                <button onClick={() => { setImageGenSection(null); setImageGenPrompt(''); }} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors" data-testid="image-gen-cancel">Cancel</button>
              </div>
              <textarea
                value={imageGenPrompt}
                onChange={e => setImageGenPrompt(e.target.value)}
                placeholder={"Describe what the image should show...\n\ne.g., A labeled diagram of Newton's 3 laws with real-world examples like a rocket, a football, and a swimmer"}
                className="w-full h-[100px] text-sm text-gray-800 leading-relaxed bg-white border border-[#34A853]/25 rounded-xl px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#34A853]/25 focus:border-[#34A853]/50 placeholder:text-gray-400 transition-all"
                style={{ fontFamily: 'inherit' }}
                autoFocus
                data-testid="image-gen-prompt"
              />
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => {
                  handleGenerateImage(imageGenSection.id, imageGenPrompt.trim() || 'auto');
                  setImageGenPrompt('');
                }} disabled={processing.startsWith('img-')} className="flex-1 h-11 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50 bg-gradient-to-r from-[#34A853] to-[#0d9488] shadow-sm hover:shadow-md transition-all" data-testid="image-gen-now">
                  {processing.startsWith('img-') ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating...</> : <><Image className="w-4 h-4" /> {imageGenPrompt.trim() ? 'Generate with Your Description' : 'Generate from Section Content'}</>}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2 text-center">Leave empty to auto-generate based on section content, or type your own description</p>
            </div>
          ) : (
            <>
              {/* Reference chip rail — visible above the composer when the
                  teacher has attached URLs or lessons as additional context
                  for the next AI message. Same look-and-feel as the chips in
                  the student tutor. */}
              {(refChips.length > 0 || chatImage) && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="teacher-chat-ref-rail">
                  {chatImage && (
                    <span data-testid="teacher-chat-image-chip"
                      className="inline-flex items-center gap-1.5 text-[10px] font-bold pl-1 pr-1 py-1 rounded-full border-2"
                      style={{ borderColor: '#F97316', background: 'color-mix(in srgb, #F97316 8%, transparent)', color: '#C2410C' }}
                      title={chatImage.title}>
                      <img src={chatImage.url} alt="" className="w-4 h-4 rounded-full object-cover" />
                      <span className="max-w-[140px] truncate">{chatImage.title || 'Image'}</span>
                      <button onClick={() => setChatImage(null)}
                        data-testid="teacher-chat-image-chip-remove"
                        aria-label="Remove attachment"
                        className="ml-0.5 hover:bg-black/10 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center">
                        <X className="w-2 h-2" />
                      </button>
                    </span>
                  )}
                  {refChips.map(r => (
                    <span key={r.id}
                      data-testid={`teacher-chat-ref-chip-${r.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-bold pl-1.5 pr-1 py-1 rounded-full border-2"
                      style={{ borderColor: '#34A853', background: 'color-mix(in srgb, #34A853 8%, transparent)', color: '#166534' }}
                      title={r.url || r.label}>
                      {r.kind === 'lesson' ? <BookOpen className="w-2.5 h-2.5" /> : <Link2 className="w-2.5 h-2.5" />}
                      <span className="max-w-[140px] truncate">{r.label}</span>
                      <button onClick={() => setRefChips(arr => arr.filter(x => x.id !== r.id))}
                        data-testid={`teacher-chat-ref-chip-remove-${r.id}`}
                        aria-label="Remove reference"
                        className="ml-0.5 hover:bg-black/10 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center">
                        <X className="w-2 h-2" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {/* Composer — single big container with the textarea on top
                  and the action toolbar tucked inside along the bottom.
                  Notion / Claude-style, focus glow on the whole shell. */}
              <div
                className="rounded-2xl transition-all shadow-sm focus-within:shadow-lg bg-white"
                style={{ border: '1.5px solid #e4e4e7' }}
                data-testid="teacher-chat-composer-shell">
                <textarea ref={chatRef} value={chatInput} onChange={handleChatInputChange} onKeyDown={handleChatKeyDown}
                  placeholder={draft.content || framework ? 'Ask AI to edit, add equations, link references, generate images…' : 'Generate content first…'}
                  disabled={!draft.content && !framework} rows={5}
                  className="w-full text-[14px] text-[#1e293b] bg-transparent rounded-2xl px-4 pt-3 pb-1 resize-none border-0 focus:outline-none placeholder:text-[#94a3b8] disabled:opacity-50 transition-all duration-200 leading-relaxed"
                  style={{ minHeight: '128px', maxHeight: '320px', fontFamily: 'inherit' }} data-testid="chat-input" />
                {/* Bottom action toolbar — inside the composer shell. */}
                <div className="px-2 pb-2 pt-1 flex items-end justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid="teacher-chat-actions-left">
                    <ReferencePopover
                      value={refChips}
                      onChange={setRefChips}
                      library={refLibrary}
                      align="left"
                      anchor="top"
                      testIdPrefix="teacher-chat-ref"
                      accent="#34A853"
                    />
                    <button
                      type="button"
                      onClick={() => chatImageInputRef.current?.click()}
                      disabled={uploadingChatImage}
                      title="Attach an image or PDF"
                      data-testid="teacher-chat-image-btn"
                      className="w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all hover:-translate-y-0.5 active:scale-95 relative flex-shrink-0 disabled:opacity-50"
                      style={{
                        borderColor: chatImage ? '#34A853' : 'rgba(0,0,0,0.10)',
                        background: chatImage ? 'color-mix(in srgb, #34A853 8%, transparent)' : 'transparent',
                        color: chatImage ? '#34A853' : '#475569',
                      }}>
                      {uploadingChatImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    </button>
                    {/* Lightweight inline hints — keep the muscle memory for
                        @ and / shortcuts, but only once the panel is wide
                        enough to fit them next to the icons (gated on the
                        panel's own resizable width, not viewport breakpoints —
                        this is a fixed-px side panel, not a viewport-relative
                        block, so md: alone doesn't reflect its real width). */}
                    {chatWidth >= 520 && (
                      <div className="hidden md:flex items-center gap-2.5 pl-1.5 border-l border-zinc-200 ml-0.5">
                        <span className="text-[10px] text-[#64748b]"><span className="font-semibold text-[#34A853]">@</span> section</span>
                        <span className="w-0.5 h-0.5 rounded-full bg-[#cbd5e1]" />
                        <span className="text-[10px] text-[#64748b]"><span className="font-semibold text-[#0d9488]">/</span> commands</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pb-0.5 flex-shrink-0 ml-auto" data-testid="teacher-chat-actions-right">
                    <ModelPicker value={chatModel} onChange={onChatModelChange} align="right" anchor="top" maxWidth={chatWidth - 140} testIdPrefix="teacher-chat-model" />
                    <Button onClick={handleChatSend} disabled={processing === 'chat' || !chatInput.trim() || (!draft.content && !framework)}
                      className="h-10 rounded-full text-white shadow-md transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 bg-gradient-to-r from-[#34A853] to-[#0d9488] px-4 font-semibold text-[12px] flex items-center gap-1.5"
                      data-testid="chat-send-btn">
                      <Send className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Send</span>
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Visual Full-Screen Modal */}
      {/* `section` is looked up fresh from the live `sections` list every render
          — visualModal itself is just the id-bearing snapshot from when the modal
          was opened, so passing it straight through would freeze the modal on
          whatever generated/section images existed at open time, only catching
          up after close+reopen. */}
      {visualModal && <VisualEditorModal section={sections.find(s => s.id === visualModal.id) || visualModal} onClose={() => setVisualModal(null)} api={api} draftId={draft.id} draftTitle={draft.title}
        onGenerateImage={(customPrompt) => handleGenerateImage(visualModal.id, customPrompt)} isGenerating={processing === `img-${visualModal.id}`}
        onImageAttached={(url, caption) => {
          setDraft(prev => {
            const fw = { ...prev.framework };
            fw.sections = (fw.sections || []).map(s => {
              if (s.id === visualModal.id) {
                return { ...s, section_images: [...(s.section_images || []), { url, caption, created_at: new Date().toISOString() }] };
              }
              return s;
            });
            return { ...prev, framework: fw };
          });
        }}
        onRemoveSectionImage={(imgIdx) => handleRemoveSectionImage(visualModal.id, imgIdx)}
        onRemoveGeneratedImage={(imgIdx) => handleRemoveGeneratedImage(visualModal.id, imgIdx)}
      />}

      {/* Equation Studio (math/physics/chemistry) */}
      <EquationStudio
        open={equationModal}
        onClose={() => setEquationModal(false)}
        subjectHint={(classification?.subject || draft?.subject || '').toLowerCase() || null}
        contextText={targetSection?.content || framework?.sections?.[0]?.content || draft?.transcript || ''}
        onInsert={(eq) => {
          // Append the equation as a structured block into the targeted section (or first one).
          const targetId = targetSection?.id || framework?.sections?.[0]?.id;
          if (!targetId) {
            toast.error('Open a section first');
            return;
          }
          let updatedSection = null;
          setDraft(prev => {
            const fw = { ...(prev.framework || {}) };
            fw.sections = (fw.sections || []).map(s => {
              if (s.id !== targetId) return s;
              const equations = [...(s.equations || []), { ...eq, created_at: new Date().toISOString() }];
              updatedSection = { equations, content: s.content || '' };
              return { ...s, equations };
            });
            return { ...prev, framework: fw };
          });
          // Persist to backend so a refresh keeps the equation
          if (updatedSection) {
            api(`/studio/framework/${draft.id}/section/${targetId}`, {
              method: 'PUT',
              body: JSON.stringify({ equations: updatedSection.equations }),
            }).catch(() => {});
          }
          triggerAutosave?.();
        }}
      />

      {/* Image Studio (Nano Banana auto-prompt + render) */}
      <ImageGenHelper
        open={!!imageHelperModal}
        onClose={() => setImageHelperModal(null)}
        draftId={draft?.id}
        sectionId={imageHelperModal?.sectionId}
        sections={(framework?.sections || []).map(s => ({ id: s.id, title: s.title }))}
        subject={(draft?.subject || '').toLowerCase() || null}
        sectionContext={(framework?.sections?.find(s => s.id === imageHelperModal?.sectionId)?.content) || ''}
        onImageReady={({ image, prompt, model, sectionId }) => {
          // Insert into the chosen section's generated_images list. sectionId comes
          // from the panel's "Add To" dropdown, falling back to the opening section.
          const sid = sectionId || imageHelperModal?.sectionId;
          if (!sid) return;
          setDraft(prev => {
            const fw = { ...(prev.framework || {}) };
            fw.sections = (fw.sections || []).map(s => {
              if (s.id !== sid) return s;
              const generated_images = [...(s.generated_images || []),
                { url: image, prompt, model, created_at: new Date().toISOString() }];
              return { ...s, generated_images };
            });
            return { ...prev, framework: fw };
          });
        }}
      />

      {/* Board Capture Panel — multi-photo OCR + audio fusion (when draft came from a Deskey session) */}
      {boardPanelOpen && draft.audio_inbox_id && (
        <BoardCapturePanel
          sessionId={draft.audio_inbox_id}
          sessionTitle={draft.title}
          onClose={() => setBoardPanelOpen(false)}
          onDraftReady={() => {
            setBoardPanelOpen(false);
            toast.success('Lesson regenerated with board photos');
            reload && reload();
          }}
        />
      )}

      {/* Preview Modal — 10 Theme Immersive View */}
      {/* Preview — renders the real student lesson viewer so teacher preview
          and student view cannot drift. The previous bespoke preview is kept
          in SessionPreviewLegacy.jsx if a fallback is ever needed. */}
      {showPreview && framework && (
        <SessionPreview
          draft={draft}
          framework={framework}
          sections={sections}
          references={draft.references || []}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Publish Modal */}
      {showPublish && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowPublish(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="publish-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Publish Content</h3>
            <p className="text-sm text-gray-500 mb-4">Select classes to publish to</p>
            <div className="space-y-2 max-h-[240px] overflow-y-auto mb-4">
              {classes.map(c => {
                const sel = selectedClasses.includes(c.id);
                return (
                  <label key={c.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${sel ? 'border-[#34A853] bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={sel} onChange={() => setSelectedClasses(prev => sel ? prev.filter(id => id !== c.id) : [...prev, c.id])} className="accent-[#34A853] w-4 h-4" />
                    <div className="flex-1"><p className="text-sm font-bold text-gray-900">{c.name}</p><p className="text-[11px] text-gray-500">{c.student_count || 0} students</p></div>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPublish(false)} className="h-9 rounded-lg text-sm">Cancel</Button>
              <Button onClick={handlePublish} disabled={processing === 'publish' || !selectedClasses.length} className="h-9 px-5 rounded-lg text-white text-sm font-bold" style={{ background: G }} data-testid="confirm-publish-btn">
                {processing === 'publish' ? 'Publishing...' : `Publish to ${selectedClasses.length} class(es)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deskey-themed Chapter + Topic publish picker (for AI-class drafts) */}
      <PublishToLessonModal
        open={showLessonPublish}
        onClose={() => setShowLessonPublish(false)}
        draft={draft}
        classes={classes}
        className={(classes.find(c => c.id === draft?.class_id)?.name) || draft?.detected_class_name || ''}
        onPublished={handleLessonPublished}
      />
    </div>
  );
}

export default ContentEditor;
